import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { notificar } from '../utils/notify';
import '../styles/bporh_admissoes.css';

// Painel de ponto do cliente (PontoMais ao vivo, API interna).
// Visão MÊS (padrão): batidas de todos os colaboradores dia a dia, acusando
// quem não bateu (dia útil sem registro) ou bateu errado (batida sem par).
// Visão DIA: batidas do dia + falta (vermelho) / sobra (verde) contra o
// horário configurado da empresa (bpo_ponto_config).

const STATUS_PONTO = {
  trabalhando: { rotulo: 'Trabalhando', classe: 'bg-success', icone: 'fa-person-running' },
  pausa: { rotulo: 'Em pausa / saiu', classe: 'bg-info text-dark', icone: 'fa-mug-hot' },
  concluido: { rotulo: 'Concluído', classe: 'bg-secondary', icone: 'fa-check' },
  sem_batida: { rotulo: 'Sem batida', classe: 'bg-light text-muted border', icone: 'fa-ban' },
  impar: { rotulo: 'Batida sem par', classe: 'bg-warning text-dark', icone: 'fa-triangle-exclamation' },
  incompleto: { rotulo: 'Faltou batida', classe: 'bg-warning text-dark', icone: 'fa-hourglass-half' },
  isento: { rotulo: 'Não registra ponto', classe: 'bg-light text-muted border', icone: 'fa-user-slash' },
  folga: { rotulo: 'Folga / DSR', classe: 'bg-light text-muted border', icone: 'fa-couch' },
  ferias: { rotulo: 'Férias', classe: 'bg-info text-dark', icone: 'fa-umbrella-beach' },
  afastamento: { rotulo: 'Afastamento', classe: 'bg-light text-muted border', icone: 'fa-notes-medical' },
};

// quem tratou o dia (férias/atestado/abono) fora do Saber — rótulo do selo
const ORIGEM_JUSTIFICATIVA = {
  rhid: 'RHiD', tiquetaque: 'TiqueTaque', pontomais: 'Ponto Mais', aghora: 'Ahgora', kairos: 'Kairos',
};

// status de um dia na visão mês
const STATUS_DIA_MES = {
  ok: { rotulo: 'OK', cor: '#116b47' },
  sem_batida: { rotulo: 'Não bateu', cor: '#a11d2b' },
  impar: { rotulo: 'Batida errada', cor: '#8a6d00' },
  incompleto: { rotulo: 'Faltou batida', cor: '#8a6d00' },
  trabalhando: { rotulo: 'Trabalhando', cor: '#116b47' },
  folga: { rotulo: 'Folga / DSR', cor: '#6c757d' },
  feriado: { rotulo: 'Feriado', cor: '#00205b' },
  isento: { rotulo: 'Não registra', cor: '#6c757d' },
  ferias: { rotulo: 'Férias', cor: '#0a6ea1' },
  afastamento: { rotulo: 'Afastamento', cor: '#7a3b9e' },
  fora: { rotulo: '—', cor: '#adb5bd' },
  // saiu no meio do mês: o resto do mês não é falta
  desligado: { rotulo: 'Desligado', cor: '#6c757d' },
};

// 'HH:MM' -> segundos
const h2s = (h) => { const p = (h || '').split(':'); return (+p[0]) * 3600 + (+p[1]) * 60; };

// segundos -> 'H:MM'
const fmtHora = (seg) => {
  const s = Math.abs(Math.round(seg));
  return `${Math.floor(s / 3600)}:${String(Math.floor((s % 3600) / 60)).padStart(2, '0')}`;
};

const hojeISO = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const BpoRhPonto = () => {
  const { clienteId } = useParams();
  const [searchParams] = useSearchParams();
  const [mensagem, setMensagem] = useState('');
  const [busca, setBusca] = useState('');
  // 'mes' é o modo de trabalho principal; 'dia' é o acompanhamento ao vivo
  const [modo, setModo] = useState('mes');
  // sistema de ponto ativo (cliente pode ter mais de um: GaussFleet tem
  // TiqueTaque + Kairos). Vem por ?sistema= no link (abre no ponto que foi
  // clicado); vazio = o backend escolhe o de maior preferência.
  const [sistema, setSistema] = useState(searchParams.get('sistema') || '');
  const [disponiveis, setDisponiveis] = useState([]);

  useEffect(() => { notificar(mensagem); }, [mensagem]);

  // ─── visão DIA ───
  const [dados, setDados] = useState(null);
  const [carregando, setCarregando] = useState(false);
  const [data, setData] = useState(hojeISO());

  const carregarDia = useCallback(() => {
    setCarregando(true);
    const accessToken = localStorage.getItem('access');
    axios
      .get(`${process.env.REACT_APP_API_BASE}/api/bpo-ponto/${clienteId}/dia/`, {
        // igual ao /mes/: consulta sempre nova, nunca do cache do navegador
        params: { data, sistema, _: Date.now() },
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'Cache-Control': 'no-cache',
          Pragma: 'no-cache',
        },
      })
      .then((res) => { setDados(res.data); setAvisoProvedor(null); setErroPonto('');
        if (res.data.disponiveis) setDisponiveis(res.data.disponiveis); })
      .catch((error) => {
        if (error.response?.status === 501) {
          setAvisoProvedor(error.response.data?.erro || 'Integração de batidas indisponível');
          if (error.response.data?.disponiveis) setDisponiveis(error.response.data.disponiveis);
          setDados(null);
          return;
        }
        setErroPonto(error.response?.data?.erro || error.response?.data?.detalhes || 'Não foi possível conectar ao ponto deste cliente.');
        setMensagem(`${error.response?.data?.erro || 'Não foi possível carregar o ponto'}`);
        setDados(null);
      })
      .finally(() => setCarregando(false));
  }, [clienteId, data, sistema]);

  // ─── visão MÊS ───
  const [avisoProvedor, setAvisoProvedor] = useState(null);   // 501: provedor sem batidas
  const [erroPonto, setErroPonto] = useState('');            // erro real da carga (não 501)
  const [dadosMes, setDadosMes] = useState(null);
  const [carregandoMes, setCarregandoMes] = useState(false);
  const [mes, setMes] = useState(hojeISO().slice(0, 7));   // AAAA-MM (atual)
  const [espelhoAberto, setEspelhoAberto] = useState(null); // colaborador do modal

  // ─── correção da jornada: a Convenia é a fonte, mas o monthly_hours de lá às
  // vezes está errado. O ajuste vive no nosso banco e vence o do espelho.
  const [jornadaEdit, setJornadaEdit] = useState(null);
  const [salvandoJornada, setSalvandoJornada] = useState(false);

  const mensalDeSeg = (seg) => {
    const mensal = Math.round((seg || 0) * 25);   // mesmo divisor do backend
    return `${Math.floor(mensal / 3600)}:${String(Math.floor((mensal % 3600) / 60)).padStart(2, '0')}`;
  };

  // O operador informa o HORÁRIO (entrada/intervalo/saída), como no contrato;
  // a diária sai daí. Mesmo modal do Resumo de ponto — o ajuste é um só.
  const abrirJornada = (c, padraoSeg) => {
    const jm = c.jornada_manual || {};
    const cfg = dadosMes?.config || {};
    setJornadaEdit({
      cpf: c.cpf,
      nome: c.nome,
      manual: Boolean(c.jornada_manual),
      entrada: jm.entrada || cfg.entrada || '08:00',
      intervalo_inicio: jm.entrada ? (jm.intervalo_inicio || '') : (cfg.intervalo_inicio || ''),
      intervalo_fim: jm.entrada ? (jm.intervalo_fim || '') : (cfg.intervalo_fim || ''),
      saida: jm.saida || cfg.saida || '18:00',
      sexta_saida: jm.sexta_saida || '',
      dias_folga: jm.dias_folga || [],
      mensalAtual: mensalDeSeg(c.esperado_seg != null ? c.esperado_seg : padraoSeg),
    });
  };

  // diária em segundos a partir dos horários do modal (prévia ao vivo)
  const jornadaSeg = (j, saidaCampo = 'saida') => {
    if (!j?.entrada || !j?.[saidaCampo]) return null;
    let seg = h2s(j[saidaCampo]) - h2s(j.entrada);
    if (j.intervalo_inicio && j.intervalo_fim) seg -= (h2s(j.intervalo_fim) - h2s(j.intervalo_inicio));
    return seg > 0 ? seg : null;
  };

  // remover=true volta para a jornada da Convenia
  const salvarJornada = (remover = false) => {
    if (!jornadaEdit?.cpf) return;
    if (!remover && (!jornadaEdit.entrada || !jornadaEdit.saida)) {
      setMensagem('Informe entrada e saída'); return;
    }
    setSalvandoJornada(true);
    const accessToken = localStorage.getItem('access');
    const corpo = remover ? { cpf: jornadaEdit.cpf, remover: true } : {
      cpf: jornadaEdit.cpf, entrada: jornadaEdit.entrada, saida: jornadaEdit.saida,
      intervalo_inicio: jornadaEdit.intervalo_inicio || '', intervalo_fim: jornadaEdit.intervalo_fim || '',
      sexta_saida: jornadaEdit.sexta_saida || '',
      dias_folga: jornadaEdit.dias_folga || [],
    };
    axios
      .post(`${process.env.REACT_APP_API_BASE}/api/bpo-ponto/${clienteId}/jornada/`, corpo,
        { headers: { Authorization: `Bearer ${accessToken}` } })
      .then((res) => {
        setMensagem(`${res.data.mensagem}`);
        setJornadaEdit(null);
        setEspelhoAberto(null);   // os totais mudam: reabre com o número novo
        carregarMes();
      })
      .catch((error) => setMensagem(
        `${error.response?.data?.mensagem || 'Não foi possível ajustar a jornada'}`))
      .finally(() => setSalvandoJornada(false));
  };

  // ─── Folga / não cobrar por dia. É a MESMA marcação do Resumo de ponto
  // (tabela bpo_ponto_abono): marcou lá, vale aqui, e vice-versa. Depois de
  // salvar, o dia e os contadores do colaborador são refeitos em memória —
  // recarregar o mês fecharia o espelho e faria a tela piscar a cada clique.
  const [marcandoDia, setMarcandoDia] = useState('');
  const aplicarAbonoLocal = (col, iso, novo) => {
    const cpf = col.cpf;
    const refazer = (c) => {
      if (String(c.pontomais_id) !== String(col.pontomais_id)) return c;
      const l = c.dias?.[iso];
      if (!l) return c;
      const diaInfo = (dadosMes?.dias || []).find(d => d.iso === iso) || {};
      let status = l.status;
      if (novo && !(l.batidas || []).length && (status === 'sem_batida' || status === 'folga')) status = 'folga';
      if (!novo && l.abono && status === 'folga') status = diaInfo.util ? 'sem_batida' : 'folga';
      const dias = { ...c.dias, [iso]: { ...l, status, abono: novo } };
      const resumo = { ok: 0, sem_batida: 0, impar: 0 };
      Object.values(dias).forEach(x => { if (x.status in resumo) resumo[x.status] += 1; });
      return { ...c, dias, resumo };
    };
    setDadosMes((prev) => {
      if (!prev) return prev;
      const mapa = { ...(prev.abonos || {}) };
      const doCpf = { ...(mapa[cpf] || {}) };
      if (novo) doCpf[iso] = novo; else delete doCpf[iso];
      if (Object.keys(doCpf).length) mapa[cpf] = doCpf; else delete mapa[cpf];
      return { ...prev, abonos: mapa, colaboradores: (prev.colaboradores || []).map(refazer) };
    });
    setEspelhoAberto((prev) => (prev ? refazer(prev) : prev));
  };
  const marcarDia = (col, iso, tipo, obs) => {
    if (!col?.cpf) {
      setMensagem('Sem CPF no cadastro deste colaborador — não dá para marcar o dia');
      return;
    }
    setMarcandoDia(iso);
    const accessToken = localStorage.getItem('access');
    const corpo = tipo ? { cpf: col.cpf, data: iso, tipo, obs } : { cpf: col.cpf, data: iso, remover: true };
    axios
      .post(`${process.env.REACT_APP_API_BASE}/api/bpo-ponto/${clienteId}/abono/`, corpo,
        { headers: { Authorization: `Bearer ${accessToken}` } })
      .then((res) => {
        setMensagem(`${res.data.mensagem}`);
        aplicarAbonoLocal(col, iso, res.data.marcado ? res.data.abono : null);
      })
      .catch((error) => setMensagem(
        `${error.response?.data?.mensagem || 'Não foi possível marcar o dia'}`))
      .finally(() => setMarcandoDia(''));
  };

  const carregarMes = useCallback(() => {
    setCarregandoMes(true);
    const accessToken = localStorage.getItem('access');
    axios
      .get(`${process.env.REACT_APP_API_BASE}/api/bpo-ponto/${clienteId}/mes/`, {
        // '_' com o instante da chamada: sem ele o navegador pode responder do
        // cache e o operador que ACABOU de corrigir uma batida no provedor
        // continuaria vendo o valor antigo depois do F5
        params: { mes, sistema, _: Date.now() },
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'Cache-Control': 'no-cache',
          Pragma: 'no-cache',
        },
      })
      .then((res) => { setDadosMes(res.data); setAvisoProvedor(null); setErroPonto('');
        if (res.data.disponiveis) setDisponiveis(res.data.disponiveis); })
      .catch((error) => {
        if (error.response?.status === 501) {
          setAvisoProvedor(error.response.data?.erro || 'Integração de batidas indisponível');
          if (error.response.data?.disponiveis) setDisponiveis(error.response.data.disponiveis);
          setDadosMes(null);
          return;
        }
        setErroPonto(error.response?.data?.erro || error.response?.data?.detalhes || 'Não foi possível conectar ao ponto deste cliente.');
        setMensagem(`${error.response?.data?.erro || 'Não foi possível carregar o mês'}`);
        setDadosMes(null);
      })
      .finally(() => setCarregandoMes(false));
  }, [clienteId, mes, sistema]);

  // ─── espelho atualizado ANTES de exibir qualquer dado ───
  // Provedores com API (PontoMais, TiqueTaque, RHiD) reimportam o cadastro de
  // colaboradores a cada abertura do painel — é a parte que envelhece (quem
  // entrou/saiu, nome, PIS). Kairos e Aghora vêm de planilha: o backend pula
  // sozinho (auto: true). As batidas em si já são buscadas ao vivo no /mes/
  // e /dia/. Sync com falha não trava: segue com o espelho existente.
  const [sincAuto, setSincAuto] = useState(true);
  useEffect(() => {
    let ativo = true;
    setSincAuto(true);
    const accessToken = localStorage.getItem('access');
    axios
      .post(`${process.env.REACT_APP_API_BASE}/api/bpo-ponto/${clienteId}/sincronizar/`,
        // forcar: ao entrar na página reimporta SEMPRE da API (sem o atalho de
        // 15 min) — o operador não precisa mais clicar em Atualizar
        { sistema, auto: true, forcar: true }, { headers: { Authorization: `Bearer ${accessToken}` } })
      .catch(() => {})
      .finally(() => {
        if (!ativo) return;
        // já marca a consulta como em andamento no mesmo render: a tela passa
        // direto de "atualizando" para "consultando", sem piscar vazia
        if (modo === 'dia') setCarregando(true); else setCarregandoMes(true);
        setSincAuto(false);
      });
    return () => { ativo = false; };
  }, [clienteId, sistema]);   // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (sincAuto) return;   // espera o espelho atualizar
    if (modo === 'dia') carregarDia();
    else carregarMes();
  }, [modo, carregarDia, carregarMes, sincAuto]);

  // Provedor EFETIVO da tela: o que veio na carga ou, quando ela falhou, o que
  // está selecionado. Amarrar isto ao dado carregado escondia o botão de
  // importar exatamente quando não havia dado — que é quando ele é necessário.
  const provedorAtual = (dadosMes || dados)?.provedor
    || sistema || (disponiveis[0] && disponiveis[0].tipo) || '';
  // Kairos e Aghora não têm API de batidas: tudo vem de planilha importada.
  const provedorManual = provedorAtual === 'kairos' || provedorAtual === 'aghora';
  // CSV importado: Aghora sempre; RHiD quando o mês tem relatório importado (API falhou na Mercatto/Setho)
  const arquivoCsv = provedorAtual === 'aghora' || (provedorAtual === 'rhid' && !!(dadosMes || dados)?.arquivo);

  // ─── configuração de horários ───
  const [configModal, setConfigModal] = useState(null);
  const [salvandoConfig, setSalvandoConfig] = useState(false);

  const abrirConfig = () => setConfigModal({ ...(dados?.config || {
    entrada: '08:00', intervalo_inicio: '12:00', intervalo_fim: '13:00', saida: '18:00',
  }) });

  const salvarConfig = () => {
    if (!configModal.entrada || !configModal.saida) {
      setMensagem('Entrada e saída são obrigatórias');
      return;
    }
    setSalvandoConfig(true);
    const accessToken = localStorage.getItem('access');
    axios
      .put(`${process.env.REACT_APP_API_BASE}/api/bpo-ponto/${clienteId}/config/`,
        configModal, { headers: { Authorization: `Bearer ${accessToken}` } })
      .then(() => {
        setMensagem('Horários salvos');
        setConfigModal(null);
        if (modo === 'dia') carregarDia();
      })
      .catch((error) => setMensagem(`${error.response?.data?.mensagem || 'Não foi possível salvar'}`))
      .finally(() => setSalvandoConfig(false));
  };

  // ─── re-sync do espelho de colaboradores ───
  const [sincronizando, setSincronizando] = useState(false);
  const sincronizar = () => {
    setSincronizando(true);
    setMensagem('Atualizando o espelho no PontoMais — pode levar um minuto...');
    const accessToken = localStorage.getItem('access');
    axios
      .post(`${process.env.REACT_APP_API_BASE}/api/bpo-ponto/${clienteId}/sincronizar/`,
        { sistema }, { headers: { Authorization: `Bearer ${accessToken}` } })
      .then((res) => {
        setMensagem(`${res.data.mensagem}`);
        if (modo === 'dia') carregarDia(); else carregarMes();
      })
      .catch((error) => setMensagem(`${error.response?.data?.erro || 'Falha no sync'}`))
      .finally(() => setSincronizando(false));
  };

  // ─── Kairos: importar o Excel do "Relatório de Ponto do Funcionário" ───
  // (o Kairos não tem API de batidas; os dados vêm desse Excel)
  const fileKairosRef = useRef(null);
  const [importandoKairos, setImportandoKairos] = useState(false);
  const importarKairos = (ev) => {
    const file = ev.target.files && ev.target.files[0];
    if (!file) return;
    setImportandoKairos(true);
    setMensagem('Lendo o Excel do Kairos — extraindo as batidas...');
    const fd = new FormData();
    fd.append('arquivo', file);
    const accessToken = localStorage.getItem('access');
    axios
      .post(`${process.env.REACT_APP_API_BASE}/api/bpo-ponto/${clienteId}/kairos/importar/`, fd,
        { headers: { Authorization: `Bearer ${accessToken}` } })
      .then((res) => {
        const p = res.data;
        setMensagem(`${p.mensagem}${p.periodo ? ` — período ${p.periodo.de} a ${p.periodo.ate}` : ''}`);
        if (modo === 'dia') carregarDia(); else carregarMes();
      })
      .catch((error) => setMensagem(`${error.response?.data?.erro || 'Falha ao importar o Excel'}`))
      .finally(() => {
        setImportandoKairos(false);
        if (fileKairosRef.current) fileKairosRef.current.value = '';
      });
  };

  // ─── Ponto Aghora / RHiD: importar o CSV exportado do portal ───
  // (o Aghora não tem API; no RHiD é o relatório quando a API não traz as batidas)
  const fileAghoraRef = useRef(null);
  const [importandoAghora, setImportandoAghora] = useState(false);
  const importarAghora = (ev) => {
    const file = ev.target.files && ev.target.files[0];
    if (!file) return;
    const rhid = provedorAtual === 'rhid';
    setImportandoAghora(true);
    setMensagem(`Lendo o CSV do ${rhid ? 'RHiD' : 'Ponto Aghora'} — extraindo as batidas...`);
    const fd = new FormData();
    fd.append('arquivo', file);
    const accessToken = localStorage.getItem('access');
    axios
      .post(`${process.env.REACT_APP_API_BASE}/api/bpo-ponto/${clienteId}/${rhid ? 'rhid' : 'aghora'}/importar/`, fd,
        { headers: { Authorization: `Bearer ${accessToken}` } })
      .then((res) => {
        const p = res.data;
        setMensagem(`${p.mensagem}${p.periodo ? ` — período ${p.periodo.de} a ${p.periodo.ate}` : ''}`);
        if (modo === 'dia') carregarDia(); else carregarMes();
      })
      .catch((error) => setMensagem(`${error.response?.data?.erro || 'Falha ao importar o CSV'}`))
      .finally(() => {
        setImportandoAghora(false);
        if (fileAghoraRef.current) fileAghoraRef.current.value = '';
      });
  };

  // ─── PontoMais: admitir colaborador novo (API interna, mesma do portal) ───
  const [admitModal, setAdmitModal] = useState(null);      // objeto do formulário
  const [admitOpcoes, setAdmitOpcoes] = useState(null);    // {equipes, cargos, turnos}
  const [salvandoAdmit, setSalvandoAdmit] = useState(false);
  const abrirAdmitir = () => {
    setAdmitModal({
      first_name: '', last_name: '', cpf: '', email: '', pis: '', matricula: '',
      is_clt: true, admission_date: hojeISO(), initial_date: '',
      team_id: '', job_title_id: '', shift_id: '',
    });
    if (!admitOpcoes) {
      const accessToken = localStorage.getItem('access');
      axios
        .get(`${process.env.REACT_APP_API_BASE}/api/bpo-ponto/${clienteId}/pontomais/opcoes/`,
          { headers: { Authorization: `Bearer ${accessToken}` } })
        .then((res) => setAdmitOpcoes(res.data))
        .catch((error) => {
          setMensagem(`${error.response?.data?.erro || 'Não foi possível carregar equipes/cargos/turnos'}`);
          setAdmitModal(null);
        });
    }
  };
  const salvarAdmitir = () => {
    const m = admitModal;
    if (!m.first_name || !m.last_name || !m.cpf || !m.pis || !m.admission_date
        || !m.team_id || !m.job_title_id || !m.shift_id) {
      setMensagem('Preencha nome, sobrenome, CPF, PIS, admissão, equipe, cargo e turno');
      return;
    }
    setSalvandoAdmit(true);
    const accessToken = localStorage.getItem('access');
    axios
      .post(`${process.env.REACT_APP_API_BASE}/api/bpo-ponto/${clienteId}/pontomais/admitir/`,
        m, { headers: { Authorization: `Bearer ${accessToken}` } })
      .then((res) => {
        setMensagem(`${res.data.nome} criado no PontoMais — clique em "Atualizar espelho" para aparecer aqui`);
        setAdmitModal(null);
      })
      .catch((error) => {
        const d = error.response?.data;
        const det = d?.detalhes ? ` — ${typeof d.detalhes === 'string' ? d.detalhes : JSON.stringify(d.detalhes)}` : '';
        setMensagem(`${d?.erro || 'Não foi possível admitir'}${det}`);
      })
      .finally(() => setSalvandoAdmit(false));
  };

  // ─── marcar/desmarcar "não bate ponto" (isenção manual) ───
  // sobrepõe a detecção automática: o colaborador sai das divergências.
  const [salvandoIsento, setSalvandoIsento] = useState(null);   // pontomais_id em curso
  const alternarIsento = (c, marcar) => {
    setSalvandoIsento(c.pontomais_id);
    const accessToken = localStorage.getItem('access');
    axios
      .post(`${process.env.REACT_APP_API_BASE}/api/bpo-ponto/${clienteId}/isento/`,
        { pontomais_id: c.pontomais_id, isento: marcar },
        { headers: { Authorization: `Bearer ${accessToken}` } })
      .then(() => {
        setMensagem(marcar
          ? `${c.nome} marcado como "não bate ponto" — fora das divergências`
          : `${c.nome} voltou a ser cobrado no ponto`);
        if (modo === 'dia') carregarDia(); else carregarMes();
      })
      .catch((error) => setMensagem(`${error.response?.data?.erro || 'Não foi possível salvar'}`))
      .finally(() => setSalvandoIsento(null));
  };

  // ─── aviso de pendência de batida por e-mail ───
  const [avisoPonto, setAvisoPonto] = useState(null);   // {item, dados?, carregando}
  const [avisoDest, setAvisoDest] = useState('');
  const [avisoOperador, setAvisoOperador] = useState('');
  const [enviandoAviso, setEnviandoAviso] = useState(false);
  // texto do aviso em edição (por cliente) + estado do painel de edição
  const [avisoEditando, setAvisoEditando] = useState(false);
  const [avisoTpl, setAvisoTpl] = useState({ assunto: '', corpo: '' });
  const [salvandoTpl, setSalvandoTpl] = useState(false);

  const abrirAviso = (item) => {
    setAvisoPonto({ item, carregando: true });
    setAvisoEditando(false);
    const accessToken = localStorage.getItem('access');
    axios
      .get(`${process.env.REACT_APP_API_BASE}/api/bpo-ponto/${clienteId}/aviso/previa/`, {
        params: { pontomais_id: item.pontomais_id, mes, sistema },
        headers: { Authorization: `Bearer ${accessToken}` },
      })
      .then((res) => {
        setAvisoPonto({ item, dados: res.data, carregando: false });
        setAvisoDest(res.data.destinatarios || '');
        setAvisoOperador(res.data.operador_sugerido_id || '');
        setAvisoTpl({
          assunto: res.data.template?.assunto || '',
          corpo: res.data.template?.corpo || '',
        });
      })
      .catch((error) => {
        setMensagem(`${error.response?.data?.erro || 'Não foi possível montar a prévia'}`);
        setAvisoPonto(null);
      });
  };

  // Re-renderiza a prévia no servidor com o texto editado — e, com salvar:true,
  // grava esse texto como o padrão deste cliente. As pendências e o operador
  // são sempre recalculados lá; aqui só vai o texto.
  const aplicarTemplateAviso = ({ salvar = false, restaurar = false, operadorId } = {}) => {
    if (!avisoPonto?.item) return;
    setSalvandoTpl(true);
    const accessToken = localStorage.getItem('access');
    axios
      .post(`${process.env.REACT_APP_API_BASE}/api/bpo-ponto/${clienteId}/aviso/previa/`, {
        pontomais_id: avisoPonto.item.pontomais_id, mes, sistema,
        // a assinatura da prévia é de quem vai assinar o envio
        operador_id: operadorId ?? avisoOperador,
        assunto: avisoTpl.assunto, corpo: avisoTpl.corpo,
        salvar, restaurar_padrao: restaurar,
      }, { headers: { Authorization: `Bearer ${accessToken}` } })
      .then((res) => {
        setAvisoPonto(a => a && ({ ...a, dados: res.data }));
        setAvisoTpl({
          assunto: res.data.template?.assunto || '',
          corpo: res.data.template?.corpo || '',
        });
        if (salvar) {
          setMensagem(restaurar
            ? 'Texto do aviso voltou ao padrão'
            : `Texto salvo — passa a ser o padrão de ${clienteNome} nos próximos avisos`);
        }
      })
      .catch((error) => setMensagem(`${error.response?.data?.erro || 'Não foi possível aplicar o texto'}`))
      .finally(() => setSalvandoTpl(false));
  };

  const enviarAviso = () => {
    const { item, dados } = avisoPonto;
    if (!avisoOperador) { setMensagem('Escolha o operador que assina o envio'); return; }
    if (!avisoDest.trim()) { setMensagem('Informe ao menos um destinatário'); return; }
    setEnviandoAviso(true);
    const accessToken = localStorage.getItem('access');
    axios
      .post(`${process.env.REACT_APP_API_BASE}/api/bpo-ponto/${clienteId}/aviso/enviar/`, {
        pontomais_id: item.pontomais_id,
        mes: dados.mes,
        operador_id: avisoOperador,
        destinatarios: avisoDest,
        assunto: dados.assunto,
        corpo_html: dados.corpo_html,
      }, { headers: { Authorization: `Bearer ${accessToken}` } })
      .then((res) => {
        setMensagem(`${res.data.mensagem}`);
        const agora = new Date().toLocaleString('pt-BR',
          { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
        setDadosMes((dm) => dm && ({
          ...dm,
          colaboradores: dm.colaboradores.map(c =>
            c.pontomais_id === item.pontomais_id
              ? { ...c, aviso_enviado_em: agora.replace(',', '') } : c),
        }));
        setAvisoPonto(null);
      })
      .catch((error) => {
        setMensagem(`${error.response?.data?.mensagem || error.response?.data?.erro || 'Falha no envio'}`);
      })
      .finally(() => setEnviandoAviso(false));
  };

  // ─── aviso por MENSAGEM (texto copiado — colaboradores que não leem e-mail) ───
  const [msgPonto, setMsgPonto] = useState(null);   // {item, dados?, carregando}
  const [msgTexto, setMsgTexto] = useState('');
  const [msgOperador, setMsgOperador] = useState('');
  const [registrandoMsg, setRegistrandoMsg] = useState(false);

  // O texto da mensagem vem PRONTO do servidor (template do cliente ou padrão,
  // com as variáveis já substituídas) — o mesmo renderizador do e-mail, para os
  // dois não divergirem. `msgTpl` guarda o template cru, que é o que se salva.
  const [msgTpl, setMsgTpl] = useState('');
  const [msgEditando, setMsgEditando] = useState(false);
  const [salvandoMsgTpl, setSalvandoMsgTpl] = useState(false);

  const abrirMensagem = (item) => {
    setMsgPonto({ item, carregando: true });
    setMsgEditando(false);
    const accessToken = localStorage.getItem('access');
    axios
      .get(`${process.env.REACT_APP_API_BASE}/api/bpo-ponto/${clienteId}/aviso/previa/`, {
        params: { pontomais_id: item.pontomais_id, mes, sistema },
        headers: { Authorization: `Bearer ${accessToken}` },
      })
      .then((res) => {
        setMsgPonto({ item, dados: res.data, carregando: false });
        setMsgOperador(res.data.operador_sugerido_id || '');
        setMsgTexto(res.data.mensagem_texto || '');
        setMsgTpl(res.data.template_msg?.corpo || '');
      })
      .catch((error) => {
        setMensagem(`${error.response?.data?.erro || 'Não foi possível montar a mensagem'}`);
        setMsgPonto(null);
      });
  };

  // Re-renderiza a mensagem no servidor (troca de operador, edição do template)
  // e, com salvar:true, grava o template como padrão do cliente.
  const aplicarMensagem = ({ operadorId, salvar = false, restaurar = false } = {}) => {
    if (!msgPonto?.item) return Promise.resolve();
    setSalvandoMsgTpl(true);
    const accessToken = localStorage.getItem('access');
    return axios
      .post(`${process.env.REACT_APP_API_BASE}/api/bpo-ponto/${clienteId}/aviso/previa/`, {
        pontomais_id: msgPonto.item.pontomais_id, mes, sistema,
        operador_id: operadorId ?? msgOperador,
        mensagem: msgTpl,
        salvar_mensagem: salvar, restaurar_padrao_msg: restaurar,
      }, { headers: { Authorization: `Bearer ${accessToken}` } })
      .then((res) => {
        setMsgPonto(m => m && ({ ...m, dados: res.data }));
        setMsgTexto(res.data.mensagem_texto || '');
        setMsgTpl(res.data.template_msg?.corpo || '');
        if (salvar) {
          setMensagem(restaurar
            ? 'Mensagem voltou ao texto padrão'
            : `Mensagem salva — passa a ser o padrão de ${clienteNome}`);
        }
      })
      .catch((error) => setMensagem(`${error.response?.data?.erro || 'Não foi possível aplicar o texto'}`))
      .finally(() => setSalvandoMsgTpl(false));
  };

  // trocar o operador reescreve a assinatura (o servidor rerenderiza o texto)
  const trocarOperadorMsg = (id) => {
    setMsgOperador(id);
    aplicarMensagem({ operadorId: id });
  };

  const copiarEMarcar = () => {
    const { item, dados } = msgPonto;
    const op = (dados.operadores || []).find(o => String(o.id) === String(msgOperador));
    setRegistrandoMsg(true);
    navigator.clipboard.writeText(msgTexto)
      .then(() => {
        const accessToken = localStorage.getItem('access');
        return axios.post(
          `${process.env.REACT_APP_API_BASE}/api/bpo-ponto/${clienteId}/aviso/registrar/`,
          { pontomais_id: item.pontomais_id, mes: dados.mes, operador: op?.nome || '' },
          { headers: { Authorization: `Bearer ${accessToken}` } });
      })
      .then(() => {
        setMensagem('Mensagem copiada — é só colar no chat. Marcado como avisado.');
        const agora = new Date().toLocaleString('pt-BR',
          { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
        setDadosMes((dm) => dm && ({
          ...dm,
          colaboradores: dm.colaboradores.map(c =>
            c.pontomais_id === item.pontomais_id
              ? { ...c, aviso_enviado_em: agora.replace(',', ''), aviso_canal: 'mensagem' } : c),
        }));
        setMsgPonto(null);
      })
      .catch((error) => {
        setMensagem(`${error.response?.data?.mensagem || error.message || 'Não foi possível copiar/registrar'}`);
      })
      .finally(() => setRegistrandoMsg(false));
  };

  const filtrar = (lista) => {
    const b = busca.trim().toLowerCase();
    if (!b) return lista;
    return lista.filter(c =>
      [c.nome, c.cargo, c.departamento, c.contratacao].filter(Boolean).join(' ').toLowerCase().includes(b));
  };

  const colaboradoresDia = React.useMemo(() => filtrar(dados?.colaboradores || []),
    [dados, busca]);                       // eslint-disable-line react-hooks/exhaustive-deps
  const colaboradoresMes = React.useMemo(() => filtrar(dadosMes?.colaboradores || []),
    [dadosMes, busca]);                    // eslint-disable-line react-hooks/exhaustive-deps

  const cfg = dados?.config;
  const clienteNome = dadosMes?.cliente || dados?.cliente;
  const ocupado = sincAuto || (modo === 'dia' ? carregando : carregandoMes);

  // espelho do colaborador: só as colunas com conteúdo (1ª E, 1ª S, 2ª E, 2ª S, +extras)
  const maxBatidas = React.useMemo(() => {
    if (!espelhoAberto || !dadosMes) return 4;
    const m = Math.max(0, ...dadosMes.dias.map(d =>
      (espelhoAberto.dias[d.iso]?.batidas || []).length));
    return Math.max(4, m % 2 ? m + 1 : m);
  }, [espelhoAberto, dadosMes]);

  return (
    <div className="bporh-ponto-page">
      <div className="container-fluid mt-4 shadow p-4 rounded bg-white">
        {/* Cabeçalho */}
        <div className="header-wrapper d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
          <div className="d-flex align-items-center">
            <i className="fas fa-stopwatch header-icon me-2"></i>
            <h2 className="titulo-pagina m-0">
              BPO RH — Ponto
              {clienteNome && <span className="text-muted"> · {clienteNome}</span>}
            </h2>
          </div>
          <div className="d-flex align-items-center gap-2 flex-wrap">
            <Link to="/BpoRh/Ponto" className="btn btn-outline-secondary">
              <i className="fas fa-arrow-left me-2"></i>
              Acessos
            </Link>

            <div className="btn-group" role="group" aria-label="Visão">
              <button type="button"
                className={`btn ${modo === 'mes' ? 'btn-primary' : 'btn-outline-primary'}`}
                onClick={() => setModo('mes')}>
                <i className="fas fa-calendar-days me-2"></i>Mês
              </button>
              <button type="button"
                className={`btn ${modo === 'dia' ? 'btn-primary' : 'btn-outline-primary'}`}
                onClick={() => setModo('dia')}>
                <i className="fas fa-calendar-day me-2"></i>Dia
              </button>
            </div>

            {disponiveis.length > 1 && (
              <select className="form-select" style={{ width: 'auto' }}
                value={sistema || disponiveis[0].tipo}
                onChange={(e) => setSistema(e.target.value)}
                title="Este cliente tem mais de um sistema de ponto">
                {disponiveis.map(d => (
                  <option key={d.tipo} value={d.tipo}>{d.rotulo}</option>
                ))}
              </select>
            )}

            {modo === 'mes' ? (
              <input type="month" className="form-control" style={{ width: 'auto' }}
                value={mes} max={hojeISO().slice(0, 7)}
                onChange={(e) => setMes(e.target.value)} />
            ) : (
              <input type="date" className="form-control" style={{ width: 'auto' }}
                value={data} max={hojeISO()}
                onChange={(e) => setData(e.target.value)} />
            )}

            <button className="btn btn-outline-primary" onClick={abrirConfig}
              disabled={ocupado} title="Horário padrão da empresa — base do falta/sobra da visão Dia">
              <i className="fas fa-gear me-2"></i>
              Configurar horários
            </button>
            <button className="btn btn-outline-warning" onClick={sincronizar}
              disabled={sincronizando}
              title="Reimporta os colaboradores do PontoMais (o espelho local é apagado e refeito)">
              <i className={`fas ${sincronizando ? 'fa-spinner fa-spin' : 'fa-rotate'} me-2`}></i>
              {sincronizando ? 'Atualizando...' : 'Atualizar espelho'}
            </button>
            {(dadosMes || dados)?.provedor === 'pontomais' && (
              <button className="btn btn-outline-success" onClick={abrirAdmitir}
                title="Criar um colaborador novo direto no PontoMais deste cliente">
                <i className="fas fa-user-plus me-2"></i>
                Admitir colaborador
              </button>
            )}
            {provedorAtual === 'kairos' && (
              <>
                <input type="file" ref={fileKairosRef} accept=".xls,.xlsx,.xlsm"
                  style={{ display: 'none' }} onChange={importarKairos} />
                <button className="btn btn-outline-success"
                  onClick={() => fileKairosRef.current && fileKairosRef.current.click()}
                  disabled={importandoKairos}
                  title="Importar o Excel do 'Relatório de Ponto do Funcionário' do Kairos">
                  <i className={`fas ${importandoKairos ? 'fa-spinner fa-spin' : 'fa-file-excel'} me-2`}></i>
                  {importandoKairos ? 'Importando...' : 'Importar Excel do Kairos'}
                </button>
              </>
            )}
            {(provedorAtual === 'aghora' || provedorAtual === 'rhid') && (
              <>
                <input type="file" ref={fileAghoraRef}
                  accept={provedorAtual === 'rhid' ? '.csv' : '.csv,.xls,.xlsx,.xlsm'}
                  style={{ display: 'none' }} onChange={importarAghora} />
                <button className="btn btn-outline-success"
                  onClick={() => fileAghoraRef.current && fileAghoraRef.current.click()}
                  disabled={importandoAghora}
                  title={provedorAtual === 'rhid'
                    ? 'Importar o relatório CSV do RHiD (a Mercatto e a Setho vêm no mesmo arquivo)'
                    : 'Importar a planilha (CSV ou Excel) exportada do Ponto Aghora'}>
                  <i className={`fas ${importandoAghora ? 'fa-spinner fa-spin' : 'fa-file-csv'} me-2`}></i>
                  {importandoAghora ? 'Importando...'
                    : provedorAtual === 'rhid' ? 'Importar relatório do RHiD' : 'Importar planilha do Aghora'}
                </button>
              </>
            )}
            <button className="btn btn-outline-primary"
              onClick={() => (modo === 'dia' ? carregarDia() : carregarMes())}
              disabled={ocupado} title="Recarregar do PontoMais">
              <i className={`fas fa-sync-alt ${ocupado ? 'fa-spin' : ''}`}></i>
            </button>
          </div>
        </div>

        {/* Kairos: as batidas também só chegam por importação — mesmo aviso
            sempre visível do Aghora, com a data da última importação */}
        {!ocupado && provedorAtual === 'kairos' && (
          (dadosMes || dados)?.precisa_importar ? (
            <div className="alert alert-danger d-flex align-items-center justify-content-between flex-wrap gap-3"
              style={{ borderLeft: '6px solid #dc3545' }}>
              <div style={{ fontSize: '1.05rem' }}>
                <i className="fas fa-triangle-exclamation me-2 fa-lg"></i>
                <b>ATENÇÃO:</b> este cliente usa o <b>Kairos, que não tem API de batidas</b> — as
                marcações <u>não aparecem aqui</u> enquanto você não
                <b> importar o Excel do "Relatório de Ponto do Funcionário"</b> deste mês.
              </div>
              <button className="btn btn-danger fw-bold"
                onClick={() => fileKairosRef.current && fileKairosRef.current.click()}
                disabled={importandoKairos}>
                <i className={`fas ${importandoKairos ? 'fa-spinner fa-spin' : 'fa-upload'} me-2`}></i>
                {importandoKairos ? 'Importando...' : 'Importar o Excel agora'}
              </button>
            </div>
          ) : (
            <div className="alert alert-warning d-flex align-items-center justify-content-between flex-wrap gap-2 py-2"
              style={{ borderLeft: '6px solid #ffc107' }}>
              <div>
                <i className="fas fa-file-excel me-2"></i>
                As batidas deste cliente vêm de <b>Excel importado</b> (o Kairos não tem API) —
                última importação em <b>{(dadosMes || dados)?.importado_em || '—'}</b>.
                Exportou relatório novo no Kairos? Reimporte para atualizar.
              </div>
              <button className="btn btn-outline-dark btn-sm"
                onClick={() => fileKairosRef.current && fileKairosRef.current.click()}
                disabled={importandoKairos}>
                <i className={`fas ${importandoKairos ? 'fa-spinner fa-spin' : 'fa-upload'} me-2`}></i>
                {importandoKairos ? 'Importando...' : 'Reimportar Excel'}
              </button>
            </div>
          )
        )}

        {/* Aghora: os dados NÃO chegam sozinhos — o aviso fica sempre visível
            para o operador lembrar de importar a planilha (pedido do Eleandro) */}
        {!ocupado && arquivoCsv && (
          (dadosMes || dados)?.precisa_importar ? (
            <div className="alert alert-danger d-flex align-items-center justify-content-between flex-wrap gap-3"
              style={{ borderLeft: '6px solid #dc3545' }}>
              <div style={{ fontSize: '1.05rem' }}>
                <i className="fas fa-triangle-exclamation me-2 fa-lg"></i>
                <b>ATENÇÃO:</b> {provedorAtual === 'rhid'
                  ? <>as batidas do <b>RHiD</b> deste cliente vêm do <b>relatório importado</b> — as</>
                  : <>este cliente usa o <b>Ponto Aghora, que não tem API</b> — as</>}
                marcações <u>não aparecem aqui</u> enquanto você não
                <b> importar a planilha exportada do portal ({provedorAtual === 'rhid' ? 'CSV' : 'CSV ou Excel'})</b> deste mês.
              </div>
              <button className="btn btn-danger fw-bold"
                onClick={() => fileAghoraRef.current && fileAghoraRef.current.click()}
                disabled={importandoAghora}>
                <i className={`fas ${importandoAghora ? 'fa-spinner fa-spin' : 'fa-upload'} me-2`}></i>
                {importandoAghora ? 'Importando...' : 'Importar a planilha agora'}
              </button>
            </div>
          ) : (
            <div className="alert alert-warning d-flex align-items-center justify-content-between flex-wrap gap-2 py-2"
              style={{ borderLeft: '6px solid #ffc107' }}>
              <div>
                <i className="fas fa-file-csv me-2"></i>
                Os dados deste cliente vêm de <b>planilha importada</b> ({provedorAtual === 'rhid'
                  ? 'relatório CSV do RHiD' : 'o Aghora não tem API'}) —
                última importação em <b>{(dadosMes || dados)?.importado_em || '—'}</b>.
                Exportou fechamento novo no portal? Reimporte para atualizar.
              </div>
              <button className="btn btn-outline-dark btn-sm"
                onClick={() => fileAghoraRef.current && fileAghoraRef.current.click()}
                disabled={importandoAghora}>
                <i className={`fas ${importandoAghora ? 'fa-spinner fa-spin' : 'fa-upload'} me-2`}></i>
                {importandoAghora ? 'Importando...' : 'Reimportar planilha'}
              </button>
            </div>
          )
        )}

        {ocupado ? (
          <div className="text-center py-5">
            <div className="spinner-border text-primary" role="status"></div>
            <p className="text-muted mt-2 mb-0">
              {sincAuto
                ? 'Atualizando os dados com o sistema de ponto (1 de 2)...'
                : 'Carregando as batidas do mês (2 de 2)...'}
            </p>
          </div>
        ) : avisoProvedor ? (
          <div className="alert alert-warning">
            <h5 className="fw-bold"><i className="fas fa-triangle-exclamation me-2"></i>Batidas indisponíveis neste ponto</h5>
            <p className="mb-2">{avisoProvedor}</p>
            <p className="mb-0 small text-muted">
              O botão <b>Atualizar espelho</b> já traz o cadastro de colaboradores.
              A leitura das marcações depende da documentação da API da Dimep — quando
              disponível, o painel de dia e mês passa a funcionar aqui automaticamente.
            </p>
          </div>
        ) : modo === 'mes' ? (
          /* ─── VISÃO MÊS ─── */
          !dadosMes ? (
            provedorManual ? (
              /* Aghora/Kairos não têm API: aqui o caminho não é conferir acesso,
                 é importar a planilha do mês — e o botão precisa estar À MÃO. */
              <div className="alert alert-warning d-flex align-items-center justify-content-between flex-wrap gap-3"
                style={{ borderLeft: '6px solid #f1be48' }}>
                <div>
                  <b><i className="fas fa-file-arrow-up me-2"></i>
                    Sem planilha importada para este mês.</b>
                  <div className="small mt-1">
                    O <b>{provedorAtual === 'aghora' ? 'Ponto Aghora' : 'Kairos'}</b> não
                    tem API de batidas — as marcações só aparecem depois que você
                    importar a planilha exportada do portal.
                  </div>
                  {erroPonto && (
                    <div className="small text-muted mt-1">{erroPonto}</div>
                  )}
                </div>
                <button className="btn btn-warning fw-bold"
                  disabled={provedorAtual === 'aghora' ? importandoAghora : importandoKairos}
                  onClick={() => (provedorAtual === 'aghora'
                    ? fileAghoraRef.current && fileAghoraRef.current.click()
                    : fileKairosRef.current && fileKairosRef.current.click())}>
                  <i className="fas fa-file-arrow-up me-2"></i>
                  Importar planilha deste mês
                </button>
              </div>
            ) : (
            <div className="alert alert-danger">
              <i className="fas fa-plug-circle-xmark me-2"></i>
              {erroPonto || 'Não foi possível carregar o ponto deste cliente.'}
              <div className="small text-muted mt-1">
                Confira o acesso do sistema de ponto em <b>Serviços → Ponto</b> deste cliente.
              </div>
            </div>
            )
          ) : (
            <>
              <div className="d-flex align-items-center gap-2 mb-3 flex-wrap">
                <span className="badge fs-6"
                  style={{ backgroundColor: '#00205b', fontVariantNumeric: 'tabular-nums' }}>
                  <i className="fas fa-calendar-days me-1"></i> {dadosMes.mes_rotulo}
                </span>
                <span className="badge bg-secondary" style={{ fontVariantNumeric: 'tabular-nums' }}>
                  {dadosMes.total} colaboradores
                </span>
                {dadosMes.com_problema > 0 ? (
                  <span className="badge bg-danger" style={{ fontVariantNumeric: 'tabular-nums' }}>
                    <i className="fas fa-triangle-exclamation me-1"></i>
                    {dadosMes.com_problema} com pendência de batida
                  </span>
                ) : (
                  <span className="badge bg-success">
                    <i className="fas fa-circle-check me-1"></i> Todos em dia
                  </span>
                )}
                <span className="badge bg-light text-dark border">
                  <i className="fas fa-clock-rotate-left me-1"></i>
                  consultado às {String(dadosMes.consultado_em).slice(-5)}
                </span>
                <input
                  type="text"
                  className="form-control form-control-sm ms-auto"
                  style={{ maxWidth: '260px' }}
                  placeholder="Nome, cargo..."
                  value={busca}
                  onChange={(e) => setBusca(e.target.value)}
                />
              </div>

              <div className="alert alert-light border small mb-3 py-2">
                <i className="fas fa-circle-info me-2 text-primary"></i>
                <b>Não bateu</b> = dia útil passado sem registro (feriados não são
                descontados). <b>Faltou batida</b> = o dia só tem 2 batidas somando bem menos
                que a jornada (ex.: só entrada e saída do almoço — faltou o par do intervalo);
                com os 4 pares completos, dia curto é atraso, não batida faltando. <b>Batida errada</b> = número ímpar de batidas no dia —
                falta entrada ou saída. Clique no colaborador para ver o espelho completo.
              </div>

              <div className="table-responsive">
                <table className="table table-striped table-hover align-middle">
                  <thead>
                    <tr>
                      <th><i className="fas fa-user me-2"></i>Colaborador</th>
                      <th><i className="fas fa-briefcase me-2"></i>Cargo</th>
                      <th><i className="fas fa-circle-check me-2"></i>Dias OK</th>
                      <th><i className="fas fa-ban me-2"></i>Não bateu</th>
                      <th><i className="fas fa-triangle-exclamation me-2"></i>Batida errada</th>
                      <th><i className="fas fa-envelope me-2"></i>Aviso</th>
                      <th className="text-center">Ações</th>
                    </tr>
                  </thead>
                  <tbody>
                    {colaboradoresMes.map((c) => (
                      <tr key={c.pontomais_id} style={{ cursor: 'pointer' }}
                        onClick={() => setEspelhoAberto(c)}>
                        <td className="fw-bold">
                          {c.nome}
                          {/* desligado no meio do mês: continua batendo até sair,
                              então aparece no relatório — mas os dias seguintes
                              não são cobrados dele */}
                          {c.desligado_em && (
                            <span className="badge ms-2"
                              style={{ backgroundColor: '#f1f3f5', color: '#6c757d',
                                       border: '1px solid #ced4da' }}
                              title={`Desligado em ${c.desligado_em} — os dias seguintes não são cobrados`}>
                              <i className="fas fa-right-from-bracket me-1"></i>
                              saiu {c.desligado_em}
                            </span>
                          )}
                          {!c.no_espelho && !c.desligado_em && (
                            <span className="badge bg-light text-muted border ms-2"
                              title="Bateu ponto mas não está no espelho — clique em Atualizar espelho">
                              fora do espelho
                            </span>
                          )}
                          {c.registra_ponto === false && (
                            <span className="badge bg-light text-muted border ms-2"
                              title={c.isento_manual
                                ? 'Marcado à mão como "não bate ponto" — fora das divergências'
                                : "Cadastrado com 'Registra ponto? Não' — não é cobrado"}>
                              <i className="fas fa-user-slash me-1"></i>não bate ponto
                            </span>
                          )}
                          {c.ausencia && (
                            <span className="badge ms-2"
                              style={{
                                backgroundColor: c.ausencia.tipo === 'ferias' ? '#e3f2fd' : '#f3e8fb',
                                color: c.ausencia.tipo === 'ferias' ? '#0a6ea1' : '#7a3b9e',
                                border: `1px solid ${c.ausencia.tipo === 'ferias' ? '#0a6ea1' : '#7a3b9e'}`,
                              }}
                              title={`${c.ausencia.motivo} (Convenia)${c.ausencia.ativo ? ' — em andamento' : ''}`}>
                              <i className={`fas ${c.ausencia.tipo === 'ferias'
                                ? 'fa-umbrella-beach' : 'fa-notes-medical'} me-1`}></i>
                              {c.ausencia.tipo === 'ferias' ? 'Férias' : 'Afastamento'} de {c.ausencia.de} até {c.ausencia.ate}
                            </span>
                          )}
                        </td>
                        <td>
                          {c.cargo || '-'}
                          {c.contratacao && (
                            <span className="badge bg-light text-muted border ms-2"
                              title="Tipo de contratação (Convenia)">
                              {c.contratacao}
                            </span>
                          )}
                        </td>
                        <td>
                          {c.registra_ponto === false && c.resumo.ok === 0 ? (
                            <span className="text-muted">—</span>
                          ) : (
                            <span className="badge bg-success" style={{ fontVariantNumeric: 'tabular-nums' }}>
                              {c.resumo.ok}
                            </span>
                          )}
                        </td>
                        <td>
                          {c.resumo.sem_batida > 0 ? (
                            <span className="badge bg-danger" style={{ fontVariantNumeric: 'tabular-nums' }}>
                              {c.resumo.sem_batida} dia{c.resumo.sem_batida === 1 ? '' : 's'}
                            </span>
                          ) : <span className="text-muted">—</span>}
                        </td>
                        <td>
                          {(c.resumo.impar > 0 || (c.resumo.incompleto || 0) > 0) ? (
                            <>
                              {c.resumo.impar > 0 && (
                                <span className="badge bg-warning text-dark me-1" style={{ fontVariantNumeric: 'tabular-nums' }}>
                                  {c.resumo.impar} dia{c.resumo.impar === 1 ? '' : 's'}
                                </span>
                              )}
                              {(c.resumo.incompleto || 0) > 0 && (
                                <span className="badge bg-warning text-dark" style={{ fontVariantNumeric: 'tabular-nums' }}
                                  title="Dias com batidas pares, mas menos horas que a jornada — faltou batida">
                                  {c.resumo.incompleto} faltou batida
                                </span>
                              )}
                            </>
                          ) : <span className="text-muted">—</span>}
                        </td>
                        <td style={{ whiteSpace: 'nowrap' }}>
                          {c.ausencia && (
                            <span className="badge me-1"
                              style={{
                                backgroundColor: c.ausencia.tipo === 'ferias' ? '#e3f2fd' : '#f3e8fb',
                                color: c.ausencia.tipo === 'ferias' ? '#0a6ea1' : '#7a3b9e',
                                border: `1px solid ${c.ausencia.tipo === 'ferias' ? '#0a6ea1' : '#7a3b9e'}`,
                              }}
                              title={`${c.ausencia.motivo} de ${c.ausencia.de} até ${c.ausencia.ate} (Convenia) — divergência pode ser por ${c.ausencia.tipo === 'ferias' ? 'férias' : 'afastamento'}`}>
                              <i className={`fas ${c.ausencia.tipo === 'ferias'
                                ? 'fa-umbrella-beach' : 'fa-notes-medical'} me-1`}></i>
                              {c.ausencia.tipo === 'ferias' ? 'Férias' : 'Afast.'} de {c.ausencia.de} até {c.ausencia.ate}
                            </span>
                          )}
                          {c.aviso_enviado_em ? (
                            <span className="badge bg-success"
                              style={{ fontVariantNumeric: 'tabular-nums' }}
                              title={`Aviso ${c.aviso_canal === 'mensagem' ? 'por mensagem copiada' : 'por e-mail'} em ${c.aviso_enviado_em} (horário de Brasília)`}>
                              <i className={`fas ${c.aviso_canal === 'mensagem'
                                ? 'fa-comment-dots' : 'fa-envelope-circle-check'} me-1`}></i>
                              avisado {c.aviso_enviado_em}
                            </span>
                          ) : (c.resumo.sem_batida + c.resumo.impar + (c.resumo.incompleto || 0)) > 0 ? (
                            !c.ausencia && <span className="badge bg-light text-muted border">não avisado</span>
                          ) : (!c.ausencia && <span className="text-muted">—</span>)}
                        </td>
                        <td className="text-center" style={{ whiteSpace: 'nowrap' }}>
                          <label className="form-check-label small text-muted me-2 d-inline-flex align-items-center gap-1"
                            style={{ cursor: 'pointer' }}
                            onClick={(e) => e.stopPropagation()}
                            title="Marque quando o colaborador não bate ponto — ele sai das divergências">
                            <input type="checkbox" className="form-check-input mt-0"
                              checked={c.registra_ponto === false}
                              disabled={salvandoIsento === c.pontomais_id}
                              onChange={(e) => { e.stopPropagation(); alternarIsento(c, e.target.checked); }} />
                            não bate ponto
                          </label>
                          {(c.resumo.sem_batida + c.resumo.impar + (c.resumo.incompleto || 0)) > 0 && (
                            <>
                              <button
                                className={`btn btn-sm me-1 ${c.aviso_enviado_em
                                  ? 'btn-outline-secondary' : 'btn-outline-warning'}`}
                                onClick={(e) => { e.stopPropagation(); abrirAviso(c); }}
                                title={c.aviso_enviado_em
                                  ? `Já avisado em ${c.aviso_enviado_em} — reenviar`
                                  : 'Enviar aviso das pendências por e-mail'}>
                                <i className="fas fa-envelope"></i>
                              </button>
                              {dadosMes?.provedor !== 'tiquetaque' && (
                                <button
                                  className={`btn btn-sm me-1 ${c.aviso_enviado_em
                                    ? 'btn-outline-secondary' : 'btn-outline-info'}`}
                                  onClick={(e) => { e.stopPropagation(); abrirMensagem(c); }}
                                  title="Mensagem pronta para copiar (WhatsApp/chat) — para quem não lê e-mail">
                                  <i className="fas fa-comment-dots"></i>
                                </button>
                              )}
                            </>
                          )}
                          <button className="btn btn-sm btn-outline-primary"
                            onClick={(e) => { e.stopPropagation(); setEspelhoAberto(c); }}
                            title="Ver o espelho do mês">
                            <i className="fas fa-table-list"></i>
                          </button>
                        </td>
                      </tr>
                    ))}
                    {colaboradoresMes.length === 0 && (
                      <tr>
                        <td colSpan={7} className="text-center text-muted py-4">
                          <i className="fas fa-filter me-2"></i>
                          Nenhum colaborador com os filtros atuais.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </>
          )
        ) : (
          /* ─── VISÃO DIA ─── */
          !dados ? (
            provedorManual ? (
              /* Aghora/Kairos não têm API: aqui o caminho não é conferir acesso,
                 é importar a planilha do mês — e o botão precisa estar À MÃO. */
              <div className="alert alert-warning d-flex align-items-center justify-content-between flex-wrap gap-3"
                style={{ borderLeft: '6px solid #f1be48' }}>
                <div>
                  <b><i className="fas fa-file-arrow-up me-2"></i>
                    Sem planilha importada para este mês.</b>
                  <div className="small mt-1">
                    O <b>{provedorAtual === 'aghora' ? 'Ponto Aghora' : 'Kairos'}</b> não
                    tem API de batidas — as marcações só aparecem depois que você
                    importar a planilha exportada do portal.
                  </div>
                  {erroPonto && (
                    <div className="small text-muted mt-1">{erroPonto}</div>
                  )}
                </div>
                <button className="btn btn-warning fw-bold"
                  disabled={provedorAtual === 'aghora' ? importandoAghora : importandoKairos}
                  onClick={() => (provedorAtual === 'aghora'
                    ? fileAghoraRef.current && fileAghoraRef.current.click()
                    : fileKairosRef.current && fileKairosRef.current.click())}>
                  <i className="fas fa-file-arrow-up me-2"></i>
                  Importar planilha deste mês
                </button>
              </div>
            ) : (
            <div className="alert alert-danger">
              <i className="fas fa-plug-circle-xmark me-2"></i>
              {erroPonto || 'Não foi possível carregar o ponto deste cliente.'}
              <div className="small text-muted mt-1">
                Confira o acesso do sistema de ponto em <b>Serviços → Ponto</b> deste cliente.
              </div>
            </div>
            )
          ) : (
            <>
              <div className="d-flex align-items-center gap-2 mb-3 flex-wrap">
                <span className="badge fs-6"
                  style={{ backgroundColor: '#00205b', fontVariantNumeric: 'tabular-nums' }}>
                  <i className="fas fa-calendar-day me-1"></i> {dados.data}
                </span>
                <span className="badge bg-secondary" style={{ fontVariantNumeric: 'tabular-nums' }}>
                  {dados.com_batida}/{dados.total} bateram ponto
                </span>
                {cfg && (
                  <span className="badge bg-light text-dark border"
                    title="Horário configurado da empresa"
                    style={{ fontVariantNumeric: 'tabular-nums' }}>
                    <i className="fas fa-gear me-1"></i>
                    {cfg.entrada}
                    {cfg.intervalo_inicio && ` – ${cfg.intervalo_inicio} / ${cfg.intervalo_fim}`}
                    {' – '}{cfg.saida}
                    {cfg.sexta_diferente && ` · sex até ${cfg.sexta_saida}`}
                    {' · '}{fmtHora(dados.esperado_seg)}h previstas
                  </span>
                )}
                <span className="badge bg-light text-dark border">
                  <i className="fas fa-clock-rotate-left me-1"></i>
                  consultado às {String(dados.consultado_em).slice(-5)}
                </span>
                <input
                  type="text"
                  className="form-control form-control-sm ms-auto"
                  style={{ maxWidth: '260px' }}
                  placeholder="Nome, cargo, departamento..."
                  value={busca}
                  onChange={(e) => setBusca(e.target.value)}
                />
              </div>

              <div className="table-responsive">
                <table className="table table-striped table-hover align-middle">
                  <thead>
                    <tr>
                      <th><i className="fas fa-user me-2"></i>Colaborador</th>
                      <th><i className="fas fa-briefcase me-2"></i>Cargo</th>
                      <th><i className="fas fa-fingerprint me-2"></i>Batidas</th>
                      <th><i className="fas fa-hourglass-half me-2"></i>Trabalhado</th>
                      <th><i className="fas fa-scale-balanced me-2"></i>Falta / Sobra</th>
                      <th><i className="fas fa-flag me-2"></i>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {colaboradoresDia.map((c) => {
                      const st = STATUS_PONTO[c.status] || STATUS_PONTO.sem_batida;
                      const sobra = c.saldo_seg >= 0;
                      return (
                        <tr key={c.pontomais_id}>
                          <td className="fw-bold">
                            {c.nome}
                            {!c.no_espelho && (
                              <span className="badge bg-light text-muted border ms-2"
                                title="Bateu ponto mas não está no espelho — clique em Atualizar espelho">
                                fora do espelho
                              </span>
                            )}
                          </td>
                          <td>
                            {c.cargo || '-'}
                            {c.contratacao && (
                              <span className="badge bg-light text-muted border ms-2"
                                title="Tipo de contratação (Convenia)">
                                {c.contratacao}
                              </span>
                            )}
                          </td>
                          <td style={{ fontVariantNumeric: 'tabular-nums' }}>
                            {c.batidas.length === 0 ? (
                              <span className="text-muted">—</span>
                            ) : (
                              c.batidas.map((h, i) => (
                                <span key={i}
                                  className="badge me-1"
                                  title={i % 2 === 0 ? 'Entrada' : 'Saída'}
                                  style={{
                                    backgroundColor: i % 2 === 0 ? '#e6f7ef' : '#fdeaec',
                                    color: i % 2 === 0 ? '#116b47' : '#a11d2b',
                                    border: `1px solid ${i % 2 === 0 ? '#1fa971' : '#dc3545'}`,
                                    fontVariantNumeric: 'tabular-nums',
                                  }}>
                                  {h}
                                </span>
                              ))
                            )}
                          </td>
                          <td style={{ fontVariantNumeric: 'tabular-nums' }}>
                            {c.batidas.length === 0 ? '—' : `${fmtHora(c.trabalhado_seg)}h`}
                          </td>
                          <td style={{ whiteSpace: 'nowrap' }}>
                            {c.status === 'isento' ? (
                              <span className="text-muted">—</span>
                            ) : (
                            <span className="badge"
                              style={{
                                backgroundColor: sobra ? '#e6f7ef' : '#fdeaec',
                                color: sobra ? '#116b47' : '#a11d2b',
                                border: `1.5px solid ${sobra ? '#1fa971' : '#dc3545'}`,
                                fontVariantNumeric: 'tabular-nums',
                                fontSize: '.85rem',
                              }}
                              title={sobra
                                ? `Sobram ${fmtHora(c.saldo_seg)} além do previsto`
                                : `Faltam ${fmtHora(c.saldo_seg)} para o previsto`}>
                              {sobra ? '+' : '−'}{fmtHora(c.saldo_seg)}h
                            </span>
                            )}
                          </td>
                          <td style={{ whiteSpace: 'nowrap' }}>
                            <span className={`badge ${st.classe}`}>
                              <i className={`fas ${st.icone} me-1`}></i>
                              {st.rotulo}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                    {colaboradoresDia.length === 0 && (
                      <tr>
                        <td colSpan={6} className="text-center text-muted py-4">
                          <i className="fas fa-filter me-2"></i>
                          Nenhum colaborador com os filtros atuais.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </>
          )
        )}
      </div>

      {/* ─── MODAL: MENSAGEM PRONTA (copiar p/ chat) ─── */}
      {msgPonto && (
        <div className="modal" tabIndex="-1"
          style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 10000 }}>
          <div className="modal-dialog modal-dialog-centered" style={{ maxWidth: '640px' }}>
            <div className="modal-content shadow-lg">
              <div className="modal-header" style={{ backgroundColor: '#ffffff', borderBottom: '1px solid #e9ecef' }}>
                <div>
                  <h5 className="modal-title mb-0 fw-bold" style={{ color: '#212529' }}>
                    <i className="fas fa-comment-dots me-2"></i>
                    Mensagem de pendência de batida
                  </h5>
                  <small style={{ color: '#6c757d' }}>
                    {msgPonto.item && `${msgPonto.item.nome} · ${clienteNome || ''}`}
                    {' · copie e cole no chat do colaborador'}
                  </small>
                </div>
                <button type="button" className="btn-close"
                  onClick={() => setMsgPonto(null)} disabled={registrandoMsg}></button>
              </div>

              <div className="modal-body">
                {msgPonto.carregando ? (
                  <div className="text-center py-5">
                    <div className="spinner-border text-primary" role="status"></div>
                    <p className="text-muted mt-2 mb-0">
                      Conferindo as divergências no PontoMais...
                    </p>
                  </div>
                ) : (
                  <>
                    <div className="mb-2">
                      <label className="form-label small fw-bold mb-1 text-muted">
                        Operador que assina (trocar regenera o texto)
                      </label>
                      <select className="form-select form-select-sm" value={msgOperador}
                        onChange={(e) => trocarOperadorMsg(e.target.value)}>
                        <option value="">Selecione...</option>
                        {(msgPonto.dados.operadores || []).map(o => (
                          <option key={o.id} value={o.id}>{o.nome}</option>
                        ))}
                      </select>
                    </div>
                    <label className="form-label small fw-bold mb-1 text-muted">
                      Mensagem (edite à vontade antes de copiar)
                    </label>
                    <textarea
                      className="form-control"
                      style={{ fontSize: '.88rem', minHeight: '260px' }}
                      value={msgTexto}
                      onChange={(e) => setMsgTexto(e.target.value)}
                    />
                    <div className="small text-muted mt-2">
                      <i className="fas fa-triangle-exclamation me-1 text-warning"></i>
                      {msgPonto.dados.pendencias.length} divergência{msgPonto.dados.pendencias.length === 1 ? '' : 's'} no
                      texto — confira antes de enviar ao colaborador.
                    </div>

                    {/* Modelo da mensagem por cliente: editar aqui muda o texto
                        de TODOS os próximos avisos deste cliente. A caixa acima
                        continua servindo para o ajuste pontual. */}
                    <div className="border rounded mt-3" style={{ backgroundColor: '#f7f9fc' }}>
                      <div className="d-flex align-items-center gap-2 px-3 py-2 border-bottom">
                        <span className="fw-bold small" style={{ color: '#00205b' }}>
                          <i className="fas fa-pen-to-square me-2"></i>Modelo da mensagem
                        </span>
                        <span className={`badge ${msgPonto.dados.template_msg?.proprio_do_cliente
                          ? 'bg-primary' : 'bg-light text-muted border'}`}>
                          {msgPonto.dados.template_msg?.proprio_do_cliente
                            ? `personalizado — ${clienteNome}` : 'texto padrão'}
                        </span>
                        <button type="button" className="btn btn-sm btn-link ms-auto p-0"
                          onClick={() => setMsgEditando(v => !v)}>
                          {msgEditando ? 'Fechar' : 'Editar modelo'}
                        </button>
                      </div>
                      {msgEditando && (
                        <div className="p-3">
                          <textarea className="form-control form-control-sm"
                            style={{ minHeight: '170px', fontSize: '.85rem', lineHeight: 1.5 }}
                            value={msgTpl} disabled={salvandoMsgTpl}
                            onChange={(e) => setMsgTpl(e.target.value)} />
                          <div className="small text-muted mt-1">
                            Variáveis: <code className="me-2">{'{{colaborador}}'}</code>
                            <code className="me-2">{'{{mes_rotulo}}'}</code>
                            <code className="me-2">{'{{lista_pendencias}}'}</code>
                            <code className="me-2">{'{{sistema_nome}}'}</code>
                            <code className="me-2">{'{{sistema_url}}'}</code>
                            <code className="me-2">{'{{empresa}}'}</code>
                            <code>{'{{operador}}'}</code>
                          </div>
                          <div className="d-flex gap-2 mt-2 flex-wrap">
                            <button type="button" className="btn btn-sm btn-outline-primary"
                              onClick={() => aplicarMensagem({ salvar: false })}
                              disabled={salvandoMsgTpl}>
                              <i className={`fas ${salvandoMsgTpl ? 'fa-spinner fa-spin' : 'fa-eye'} me-2`}></i>
                              Aplicar acima
                            </button>
                            <button type="button" className="btn btn-sm btn-success"
                              onClick={() => aplicarMensagem({ salvar: true })}
                              disabled={salvandoMsgTpl}>
                              <i className="fas fa-floppy-disk me-2"></i>
                              Salvar para {clienteNome}
                            </button>
                            {msgPonto.dados.template_msg?.proprio_do_cliente && (
                              <button type="button" className="btn btn-sm btn-outline-secondary"
                                onClick={() => aplicarMensagem({ salvar: true, restaurar: true })}
                                disabled={salvandoMsgTpl}>
                                <i className="fas fa-rotate-left me-2"></i>
                                Restaurar padrão
                              </button>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  </>
                )}
              </div>

              <div className="modal-footer bg-light">
                <button type="button" className="btn btn-secondary"
                  onClick={() => setMsgPonto(null)} disabled={registrandoMsg}>
                  <i className="fas fa-times me-2"></i>
                  Cancelar
                </button>
                {!msgPonto.carregando && (
                  <button type="button" className="btn btn-success"
                    onClick={copiarEMarcar} disabled={registrandoMsg || !msgTexto.trim()}>
                    <i className={`fas ${registrandoMsg ? 'fa-spinner fa-spin' : 'fa-copy'} me-2`}></i>
                    {registrandoMsg ? 'Registrando...' : 'Copiar e marcar como avisado'}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ─── MODAL: AVISO DE PENDÊNCIA DE BATIDA (prévia + confirmação) ─── */}
      {avisoPonto && (
        <div className="modal" tabIndex="-1"
          style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 10000 }}>
          <div className="modal-dialog modal-lg modal-dialog-centered">
            <div className="modal-content shadow-lg">
              <div className="modal-header" style={{ backgroundColor: '#ffffff', borderBottom: '1px solid #e9ecef' }}>
                <div>
                  <h5 className="modal-title mb-0 fw-bold" style={{ color: '#212529' }}>
                    <i className="fas fa-envelope me-2"></i>
                    Aviso de pendência de batida
                  </h5>
                  <small style={{ color: '#6c757d' }}>
                    {avisoPonto.item && `${avisoPonto.item.nome} · ${clienteNome || ''}`}
                    {avisoPonto.item?.aviso_enviado_em && ` · já avisado em ${avisoPonto.item.aviso_enviado_em}`}
                  </small>
                </div>
                <button type="button" className="btn-close"
                  onClick={() => setAvisoPonto(null)} disabled={enviandoAviso}></button>
              </div>

              <div className="modal-body">
                {avisoPonto.carregando ? (
                  <div className="text-center py-5">
                    <div className="spinner-border text-primary" role="status"></div>
                    <p className="text-muted mt-2 mb-0">
                      Conferindo as pendências no PontoMais e montando a prévia...
                    </p>
                  </div>
                ) : (
                  <>
                    <div className="row g-2 mb-3">
                      <div className="col-md-7">
                        <label className="form-label small fw-bold mb-1 text-muted">
                          Destinatários (edite à vontade — separe por ; )
                        </label>
                        <input type="text" className="form-control form-control-sm"
                          value={avisoDest}
                          onChange={(e) => setAvisoDest(e.target.value)}
                          placeholder="email@exemplo.com; outro@exemplo.com" />
                        {!avisoPonto.dados.destinatarios && (
                          <small className="text-muted">
                            Colaborador sem e-mail no espelho — informe manualmente.
                          </small>
                        )}
                      </div>
                      <div className="col-md-5">
                        <label className="form-label small fw-bold mb-1 text-muted">
                          Enviar com a conta de
                        </label>
                        {/* trocar quem assina refaz a prévia — a assinatura do
                            e-mail tem de ser de quem realmente envia */}
                        <select className="form-select form-select-sm" value={avisoOperador}
                          onChange={(e) => {
                            setAvisoOperador(e.target.value);
                            aplicarTemplateAviso({ operadorId: e.target.value });
                          }}>
                          <option value="">Selecione o operador...</option>
                          {(avisoPonto.dados.operadores || []).map(o => (
                            <option key={o.id} value={o.id} disabled={!o.pronto}>
                              {o.nome}{!o.pronto ? ' (sem senha de app)' : ''}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>

                    {/* ── Texto do aviso: editável e salvável POR CLIENTE ──
                        Cada empresa tem seu tom e suas regras; o texto salvo
                        passa a ser o padrão dela nos próximos avisos. */}
                    <div className="border rounded mb-3" style={{ backgroundColor: '#f7f9fc' }}>
                      <div className="d-flex align-items-center gap-2 px-3 py-2 border-bottom">
                        <span className="fw-bold small" style={{ color: '#00205b' }}>
                          <i className="fas fa-pen-to-square me-2"></i>Texto do aviso
                        </span>
                        <span className={`badge ${avisoPonto.dados.template?.proprio_do_cliente
                          ? 'bg-primary' : 'bg-light text-muted border'}`}>
                          {avisoPonto.dados.template?.proprio_do_cliente
                            ? `personalizado — ${clienteNome}` : 'texto padrão'}
                        </span>
                        <button type="button" className="btn btn-sm btn-link ms-auto p-0"
                          onClick={() => setAvisoEditando(v => !v)}>
                          {avisoEditando ? 'Fechar edição' : 'Editar texto'}
                        </button>
                      </div>
                      {avisoEditando && (
                        <div className="p-3">
                          <label className="form-label small fw-bold mb-1">Assunto</label>
                          <input type="text" className="form-control form-control-sm mb-2"
                            value={avisoTpl.assunto} disabled={salvandoTpl}
                            onChange={(e) => setAvisoTpl(t => ({ ...t, assunto: e.target.value }))} />
                          <label className="form-label small fw-bold mb-1">
                            Corpo do e-mail
                          </label>
                          <textarea className="form-control form-control-sm"
                            style={{ minHeight: '190px', fontSize: '.85rem', lineHeight: 1.5 }}
                            value={avisoTpl.corpo} disabled={salvandoTpl}
                            onChange={(e) => setAvisoTpl(t => ({ ...t, corpo: e.target.value }))} />
                          <div className="small text-muted mt-1">
                            Escreva o texto normalmente — sem código. Deixe uma <b>linha em
                            branco</b> entre um parágrafo e outro; a formatação do e-mail
                            (cores, logo, assinatura) é aplicada sozinha.
                            <div className="mt-1">
                              Os nomes entre chaves são trocados automaticamente:{' '}
                              {(avisoPonto.dados.template?.variaveis || []).map(x => (
                                <code key={x} className="me-2">{`{{${x}}}`}</code>
                              ))}
                            </div>
                            <div className="mt-1">
                              <code>{'{{tabela_pendencias}}'}</code> vira a tabela dos dias e{' '}
                              <code>{'{{botao_sistema}}'}</code> o botão de acesso — deixe cada
                              um sozinho na sua linha.
                            </div>
                          </div>
                          <div className="d-flex gap-2 mt-2 flex-wrap">
                            <button type="button" className="btn btn-sm btn-outline-primary"
                              onClick={() => aplicarTemplateAviso({ salvar: false })}
                              disabled={salvandoTpl}>
                              <i className={`fas ${salvandoTpl ? 'fa-spinner fa-spin' : 'fa-eye'} me-2`}></i>
                              Ver prévia
                            </button>
                            <button type="button" className="btn btn-sm btn-success"
                              onClick={() => aplicarTemplateAviso({ salvar: true })}
                              disabled={salvandoTpl}>
                              <i className="fas fa-floppy-disk me-2"></i>
                              Salvar para {clienteNome}
                            </button>
                            {avisoPonto.dados.template?.proprio_do_cliente && (
                              <button type="button" className="btn btn-sm btn-outline-secondary"
                                onClick={() => aplicarTemplateAviso({ salvar: true, restaurar: true })}
                                disabled={salvandoTpl}
                                title="Remove o texto personalizado e volta ao padrão">
                                <i className="fas fa-rotate-left me-2"></i>
                                Restaurar padrão
                              </button>
                            )}
                          </div>
                        </div>
                      )}
                    </div>

                    <div className="small text-muted mb-1">
                      <b>Assunto:</b> {avisoPonto.dados.assunto}
                      <span className="ms-2 badge bg-warning text-dark"
                        style={{ fontVariantNumeric: 'tabular-nums' }}>
                        {avisoPonto.dados.pendencias.length} pendência{avisoPonto.dados.pendencias.length === 1 ? '' : 's'}
                      </span>
                    </div>
                    {(avisoPonto.dados.anexos || []).length > 0 && (
                      <div className="small text-muted mb-1">
                        <i className="fas fa-paperclip me-1"></i><b>Anexo:</b>{' '}
                        {avisoPonto.dados.anexos.map((a) => `${a.nome} (${Math.round(a.tamanho / 1024)} KB)`).join(', ')}
                        <span className="ms-1">— vai junto em todo aviso desta empresa</span>
                      </div>
                    )}
                    <div className="border rounded" style={{ height: '420px', overflow: 'hidden' }}>
                      <iframe title="Prévia do aviso de ponto"
                        srcDoc={avisoPonto.dados.corpo_previa}
                        style={{ width: '100%', height: '100%', border: 0 }} />
                    </div>
                  </>
                )}
              </div>

              <div className="modal-footer bg-light">
                <button type="button" className="btn btn-secondary"
                  onClick={() => setAvisoPonto(null)} disabled={enviandoAviso}>
                  <i className="fas fa-times me-2"></i>
                  Cancelar
                </button>
                {!avisoPonto.carregando && (
                  <button type="button" className="btn btn-success"
                    onClick={enviarAviso} disabled={enviandoAviso}>
                    <i className={`fas ${enviandoAviso ? 'fa-spinner fa-spin' : 'fa-paper-plane'} me-2`}></i>
                    {enviandoAviso ? 'Enviando...'
                      : avisoPonto.item?.aviso_enviado_em ? 'Confirmar reenvio' : 'Confirmar envio'}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ─── MODAL: ESPELHO DO MÊS DO COLABORADOR ─── */}
      {espelhoAberto && dadosMes && (
        <div className="modal" tabIndex="-1"
          style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 9999 }}>
          <div className="modal-dialog modal-xl modal-dialog-centered modal-dialog-scrollable"
            style={{ maxWidth: 'min(1280px, 96vw)' }}>
            <div className="modal-content shadow-lg">
              <div className="modal-header" style={{ backgroundColor: '#ffffff', borderBottom: '1px solid #e9ecef' }}>
                <div>
                  <h5 className="modal-title mb-0 fw-bold" style={{ color: '#212529' }}>
                    <i className="fas fa-table-list me-2"></i>
                    {espelhoAberto.nome}
                  </h5>
                  <small style={{ color: '#6c757d' }}>
                    {dadosMes.cliente} · espelho de {dadosMes.mes_rotulo}
                    {espelhoAberto.cargo && ` · ${espelhoAberto.cargo}`}
                    {espelhoAberto.contratacao && ` · ${espelhoAberto.contratacao}`}
                    {' · '}jornada{' '}
                    {fmtHora(espelhoAberto.esperado_seg != null
                      ? espelhoAberto.esperado_seg : dadosMes.esperado_seg)}/dia
                    {espelhoAberto.esperado_sexta_seg
                      ? ` · sex ${fmtHora(espelhoAberto.esperado_sexta_seg)}` : ''}
                    {espelhoAberto.jornada_manual
                      ? ' (ajustada à mão)'
                      : espelhoAberto.esperado_seg != null ? ' (Convenia)' : ' (config da empresa)'}
                    {espelhoAberto.cpf && (
                      <button type="button" className="btn btn-link btn-sm p-0 ms-1"
                        style={{ fontSize: '.75rem', textDecoration: 'none' }}
                        onClick={() => abrirJornada(espelhoAberto, dadosMes.esperado_seg)}
                        title="Corrigir a jornada deste colaborador">
                        <i className="fas fa-pen-to-square"></i>
                      </button>
                    )}
                  </small>
                </div>
                <button type="button" className="btn-close" onClick={() => setEspelhoAberto(null)}></button>
              </div>
              <div className="modal-body">
                <div className="table-responsive">
                  <table className="table table-sm table-striped align-middle mb-0">
                    <thead>
                      <tr>
                        <th>Data</th>
                        {Array.from({ length: maxBatidas }, (_, i) => (
                          <th key={i} style={{ fontVariantNumeric: 'tabular-nums' }}>
                            {Math.floor(i / 2) + 1}ª {i % 2 === 0 ? 'Entrada' : 'Saída'}
                          </th>
                        ))}
                        <th>Status</th>
                        <th className="text-center"
                          title="Marque para não cobrar este dia do colaborador (folga combinada, compensação, dia de campo). Vale também no Resumo de ponto.">
                          Não cobrar
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {dadosMes.dias.map((dia) => {
                        const l = espelhoAberto.dias[dia.iso] || { batidas: [], status: 'fora' };
                        if (l.status === 'fora') return null;
                        const st = STATUS_DIA_MES[l.status] || STATUS_DIA_MES.fora;
                        const problema = l.status === 'sem_batida' || l.status === 'impar';
                        return (
                          <tr key={dia.iso}
                            style={{
                              backgroundColor: l.status === 'sem_batida' ? '#fdeaec'
                                : l.status === 'impar' ? '#fff8e6'
                                : l.status === 'ferias' ? '#e3f2fd'
                                : l.status === 'afastamento' ? '#f3e8fb' : undefined,
                              opacity: l.status === 'folga' ? .55 : 1,
                            }}>
                            <td style={{ whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>
                              {dia.semana}, {String(dia.dia).padStart(2, '0')}/{dadosMes.mes.slice(5)}
                              {dia.feriado && (
                                <div className="small fw-semibold" style={{ color: '#00205b' }}
                                  title={l.batidas.length ? 'Trabalhou em feriado: não há jornada a cumprir, as horas contam como sobra' : 'Feriado: não há jornada a cumprir'}>
                                  <i className="fas fa-star me-1"></i>feriado · {dia.feriado}
                                </div>
                              )}
                            </td>
                            {Array.from({ length: maxBatidas }, (_, i) => (
                              <td key={i} style={{ fontVariantNumeric: 'tabular-nums' }}>
                                {l.batidas[i] || <span className="text-muted">—</span>}
                              </td>
                            ))}
                            <td style={{ whiteSpace: 'nowrap' }}>
                              <span className="badge"
                                style={{
                                  backgroundColor: problema ? (l.status === 'sem_batida' ? '#dc3545' : '#f1be48') : 'transparent',
                                  color: problema ? (l.status === 'sem_batida' ? '#fff' : '#4a3800') : st.cor,
                                  border: problema ? 'none' : `1px solid ${st.cor}`,
                                }}>
                                {l.abono && l.status === 'folga'
                                  ? (l.abono.tipo === 'nao_cobrar' ? 'Não cobrar' : 'Folga')
                                  : st.rotulo}
                              </span>
                              {/* lançamento da apuração do RHiD (férias, atestado, Day Off):
                                  mostra o texto que o RH digitou lá; 'parcial' = houve
                                  batida e só parte do dia foi justificada */}
                              {l.justificativa && (
                                <span className="badge ms-1"
                                  style={{ background: '#eef2f8', color: '#00205b', border: '1px solid #c9d3e6', fontWeight: 600 }}
                                  title={l.justificativa.parcial
                                    ? 'Parte do dia foi justificada no sistema de ponto (as batidas cobrem o restante)'
                                    : 'Dia tratado no sistema de ponto — não é falta'}>
                                  <i className="fas fa-clipboard-check me-1"></i>
                                  {l.justificativa.parcial ? 'parcial · ' : ''}{l.justificativa.texto} ({ORIGEM_JUSTIFICATIVA[l.justificativa.origem] || l.justificativa.origem})
                                </span>
                              )}
                              {/* bateu ponto em dia de férias/afastamento: o dia
                                  continua sendo ausência, mas o RH precisa ver */}
                              {l.em_ausencia && (
                                <span className="badge ms-1"
                                  style={{ background: '#fff1e6', color: '#b5560e', border: '1px solid #f0a868' }}
                                  title="Há batidas neste dia, mas o colaborador está de férias/afastado na Convenia — confira se trabalhou mesmo ou se a ausência está errada">
                                  <i className="fas fa-triangle-exclamation me-1"></i>bateu ponto
                                </span>
                              )}
                            </td>
                            <td className="text-center" style={{ minWidth: 190 }}>
                              {/* marcar = folga na hora, sem diálogo. Com motivo ao
                                  lado o dia vira 'não cobrar' e a justificativa fica
                                  no histórico — igual ao Resumo de ponto. */}
                              {(l.status === 'isento' || l.status === 'ferias' || l.status === 'afastamento' || l.status === 'feriado' || l.status === 'desligado') ? (
                                <span className="text-muted">—</span>
                              ) : (
                                <div className="d-flex align-items-center gap-2">
                                  <input type="checkbox" className="form-check-input mt-0"
                                    checked={!!l.abono}
                                    disabled={marcandoDia === dia.iso || !espelhoAberto.cpf}
                                    onChange={(e) => marcarDia(espelhoAberto, dia.iso,
                                      e.target.checked ? 'folga' : null)}
                                    title={!espelhoAberto.cpf
                                      ? 'Sem CPF no cadastro — não dá para marcar'
                                      : (l.abono
                                        ? `${l.abono.tipo === 'folga' ? 'Folga' : 'Não cobrar'}${
                                            l.abono.autor ? ` — ${l.abono.autor}` : ''}`
                                        : 'Não cobrar este dia')} />
                                  {l.abono && (
                                    <input type="text" className="form-control form-control-sm"
                                      style={{ fontSize: '.78rem' }}
                                      defaultValue={l.abono.obs || ''}
                                      key={`ab-${dia.iso}-${l.abono.obs || ''}`}
                                      placeholder="motivo (opcional)"
                                      disabled={marcandoDia === dia.iso}
                                      onBlur={(e) => {
                                        const motivo = e.target.value.trim();
                                        if (motivo === (l.abono.obs || '')) return;
                                        marcarDia(espelhoAberto, dia.iso,
                                          motivo ? 'nao_cobrar' : 'folga', motivo);
                                      }}
                                      title="Com motivo o dia fica registrado como 'não cobrar'; em branco, como folga" />
                                  )}
                                </div>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
              <div className="modal-footer bg-light">
                <span className="me-auto small text-muted" style={{ fontVariantNumeric: 'tabular-nums' }}>
                  {espelhoAberto.resumo.ok} OK · {espelhoAberto.resumo.sem_batida} sem batida ·{' '}
                  {espelhoAberto.resumo.impar} com batida errada
                  {(espelhoAberto.resumo.incompleto || 0) > 0 && ` · ${espelhoAberto.resumo.incompleto} com batida faltando`}
                </span>
                <button type="button" className="btn btn-secondary" onClick={() => setEspelhoAberto(null)}>
                  Fechar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ─── MODAL: CONFIGURAR HORÁRIOS ─── */}
      {configModal && (
        <div className="modal" tabIndex="-1"
          style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 9999 }}>
          <div className="modal-dialog modal-dialog-centered" style={{ maxWidth: '520px' }}>
            <div className="modal-content shadow-lg">
              <div className="modal-header" style={{ backgroundColor: '#ffffff', borderBottom: '1px solid #e9ecef' }}>
                <div>
                  <h5 className="modal-title mb-0 fw-bold" style={{ color: '#212529' }}>
                    <i className="fas fa-gear me-2"></i>
                    Horários da empresa
                  </h5>
                  <small style={{ color: '#6c757d' }}>
                    {clienteNome} — base do cálculo de falta/sobra
                  </small>
                </div>
                <button type="button" className="btn-close" onClick={() => setConfigModal(null)}></button>
              </div>
              <div className="modal-body">
                <div className="row g-3">
                  <div className="col-6">
                    <label className="form-label fw-bold small">Entrada *</label>
                    <input type="time" className="form-control" value={configModal.entrada || ''}
                      onChange={(e) => setConfigModal(m => ({ ...m, entrada: e.target.value }))} />
                  </div>
                  <div className="col-6">
                    <label className="form-label fw-bold small">Saída *</label>
                    <input type="time" className="form-control" value={configModal.saida || ''}
                      onChange={(e) => setConfigModal(m => ({ ...m, saida: e.target.value }))} />
                  </div>
                  <div className="col-12">
                    <label className="form-label fw-bold small mb-1">Batidas no dia</label>
                    <div className="d-flex gap-2">
                      {(() => {
                        const corrida = !(jornadaEdit.intervalo_inicio && jornadaEdit.intervalo_fim
                          && jornadaEdit.intervalo_inicio !== jornadaEdit.intervalo_fim);
                        return (<>
                          <button type="button" className={`btn btn-sm ${!corrida ? 'btn-secondary' : 'btn-light border'}`}
                            onClick={() => setJornadaEdit(j => ({ ...j,
                              intervalo_inicio: (j.intervalo_inicio && j.intervalo_inicio !== j.intervalo_fim) ? j.intervalo_inicio : '12:00',
                              intervalo_fim: (j.intervalo_fim && j.intervalo_inicio !== j.intervalo_fim) ? j.intervalo_fim : '13:00' }))}>
                            <i className="fas fa-grip me-1" aria-hidden="true"></i>4 — com intervalo
                          </button>
                          <button type="button" className={`btn btn-sm ${corrida ? 'btn-secondary' : 'btn-light border'}`}
                            title="Só entrada e saída (estágio, meio período): 2 batidas não conta como batida faltando"
                            onClick={() => setJornadaEdit(j => ({ ...j, intervalo_inicio: '', intervalo_fim: '' }))}>
                            <i className="fas fa-right-left me-1" aria-hidden="true"></i>2 — jornada corrida
                          </button>
                        </>);
                      })()}
                    </div>
                    <div className="form-text">Com "2 — jornada corrida", o dia com só entrada e saída é normal; sair mais cedo vira saldo, não "faltou batida".</div>
                  </div>
                  <div className="col-6">
                    <label className="form-label fw-bold small">Início do intervalo</label>
                    <input type="time" className="form-control" value={configModal.intervalo_inicio || ''}
                      onChange={(e) => setConfigModal(m => ({ ...m, intervalo_inicio: e.target.value }))} />
                  </div>
                  <div className="col-6">
                    <label className="form-label fw-bold small">Fim do intervalo</label>
                    <input type="time" className="form-control" value={configModal.intervalo_fim || ''}
                      onChange={(e) => setConfigModal(m => ({ ...m, intervalo_fim: e.target.value }))} />
                  </div>
                </div>
                <hr className="my-3" />
                <div className="d-flex align-items-center gap-2 mb-2">
                  <span className="fw-bold small">Sexta-feira</span>
                  <span className="badge bg-light text-muted border">opcional</span>
                  {configModal.sexta_saida && (
                    <button type="button" className="btn btn-sm btn-link p-0 ms-auto"
                      onClick={() => setConfigModal(m => ({ ...m, sexta_entrada: '', sexta_intervalo_inicio: '',
                        sexta_intervalo_fim: '', sexta_saida: '' }))}>
                      igual aos outros dias
                    </button>
                  )}
                </div>
                <div className="row g-3">
                  <div className="col-6">
                    <label className="form-label small">Entrada (sexta)</label>
                    <input type="time" className="form-control form-control-sm" value={configModal.sexta_entrada || ''}
                      placeholder={configModal.entrada}
                      onChange={(e) => setConfigModal(m => ({ ...m, sexta_entrada: e.target.value }))} />
                  </div>
                  <div className="col-6">
                    <label className="form-label small">Saída (sexta)</label>
                    <input type="time" className="form-control form-control-sm" value={configModal.sexta_saida || ''}
                      onChange={(e) => setConfigModal(m => ({ ...m, sexta_saida: e.target.value }))} />
                  </div>
                  <div className="col-6">
                    <label className="form-label small">Início do intervalo (sexta)</label>
                    <input type="time" className="form-control form-control-sm" value={configModal.sexta_intervalo_inicio || ''}
                      onChange={(e) => setConfigModal(m => ({ ...m, sexta_intervalo_inicio: e.target.value }))} />
                  </div>
                  <div className="col-6">
                    <label className="form-label small">Fim do intervalo (sexta)</label>
                    <input type="time" className="form-control form-control-sm" value={configModal.sexta_intervalo_fim || ''}
                      onChange={(e) => setConfigModal(m => ({ ...m, sexta_intervalo_fim: e.target.value }))} />
                  </div>
                </div>
                <div className="alert alert-light border small mt-3 mb-0">
                  <i className="fas fa-circle-info me-2 text-primary"></i>
                  O intervalo é descontado da jornada. Deixe os dois campos de intervalo em
                  branco para jornada corrida. Na <b>sexta-feira</b>, preencha só a saída se a
                  diferença for sair mais cedo — entrada e intervalo são herdados dos outros dias.
                </div>
              </div>
              <div className="modal-footer bg-light">
                <button type="button" className="btn btn-secondary"
                  onClick={() => setConfigModal(null)} disabled={salvandoConfig}>
                  Cancelar
                </button>
                <button type="button" className="btn btn-primary"
                  onClick={salvarConfig} disabled={salvandoConfig}>
                  <i className={`fas ${salvandoConfig ? 'fa-spinner fa-spin' : 'fa-check'} me-2`}></i>
                  Salvar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ─── MODAL: admitir colaborador no PontoMais ─── */}
      {admitModal && (
        <div className="modal" tabIndex="-1"
          style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 9999 }}>
          <div className="modal-dialog modal-dialog-centered" style={{ maxWidth: '640px' }}>
            <div className="modal-content shadow-lg">
              <div className="modal-header" style={{ backgroundColor: '#ffffff', borderBottom: '1px solid #e9ecef' }}>
                <div>
                  <h5 className="modal-title mb-0 fw-bold" style={{ color: '#212529' }}>
                    <i className="fas fa-user-plus me-2"></i>
                    Admitir colaborador
                  </h5>
                  <small style={{ color: '#6c757d' }}>
                    {clienteNome} — criado direto no PontoMais
                  </small>
                </div>
                <button type="button" className="btn-close" onClick={() => setAdmitModal(null)}></button>
              </div>
              <div className="modal-body">
                {!admitOpcoes ? (
                  <div className="text-center py-4">
                    <div className="spinner-border text-primary" role="status"></div>
                    <p className="text-muted mt-2 mb-0">Carregando equipes, cargos e turnos...</p>
                  </div>
                ) : (
                  <div className="row g-3">
                    <div className="col-6">
                      <label className="form-label fw-bold small">Primeiro nome *</label>
                      <input type="text" className="form-control" value={admitModal.first_name}
                        onChange={(e) => setAdmitModal(m => ({ ...m, first_name: e.target.value }))} />
                    </div>
                    <div className="col-6">
                      <label className="form-label fw-bold small">Sobrenome *</label>
                      <input type="text" className="form-control" value={admitModal.last_name}
                        onChange={(e) => setAdmitModal(m => ({ ...m, last_name: e.target.value }))} />
                    </div>
                    <div className="col-6">
                      <label className="form-label fw-bold small">CPF *</label>
                      <input type="text" className="form-control" placeholder="000.000.000-00"
                        value={admitModal.cpf}
                        onChange={(e) => setAdmitModal(m => ({ ...m, cpf: e.target.value }))} />
                    </div>
                    <div className="col-6">
                      <label className="form-label fw-bold small">PIS *</label>
                      <input type="text" className="form-control" value={admitModal.pis}
                        onChange={(e) => setAdmitModal(m => ({ ...m, pis: e.target.value }))} />
                    </div>
                    <div className="col-6">
                      <label className="form-label fw-bold small">E-mail</label>
                      <input type="email" className="form-control" value={admitModal.email}
                        onChange={(e) => setAdmitModal(m => ({ ...m, email: e.target.value }))} />
                    </div>
                    <div className="col-6">
                      <label className="form-label fw-bold small">Matrícula</label>
                      <input type="text" className="form-control" value={admitModal.matricula}
                        onChange={(e) => setAdmitModal(m => ({ ...m, matricula: e.target.value }))} />
                    </div>
                    <div className="col-6">
                      <label className="form-label fw-bold small">Data de admissão *</label>
                      <input type="date" className="form-control" value={admitModal.admission_date}
                        onChange={(e) => setAdmitModal(m => ({ ...m, admission_date: e.target.value }))} />
                    </div>
                    <div className="col-6">
                      <label className="form-label fw-bold small">Início no controle de ponto</label>
                      <input type="date" className="form-control" value={admitModal.initial_date}
                        onChange={(e) => setAdmitModal(m => ({ ...m, initial_date: e.target.value }))} />
                      <small className="text-muted">Vazio = mesma data da admissão</small>
                    </div>
                    <div className="col-6">
                      <label className="form-label fw-bold small">Equipe *</label>
                      <select className="form-select" value={admitModal.team_id}
                        onChange={(e) => setAdmitModal(m => ({ ...m, team_id: e.target.value }))}>
                        <option value="">Selecione...</option>
                        {(admitOpcoes.equipes || []).map(o => (
                          <option key={o.id} value={o.id}>{o.nome}</option>
                        ))}
                      </select>
                    </div>
                    <div className="col-6">
                      <label className="form-label fw-bold small">Cargo *</label>
                      <select className="form-select" value={admitModal.job_title_id}
                        onChange={(e) => setAdmitModal(m => ({ ...m, job_title_id: e.target.value }))}>
                        <option value="">Selecione...</option>
                        {(admitOpcoes.cargos || []).map(o => (
                          <option key={o.id} value={o.id}>{o.nome}</option>
                        ))}
                      </select>
                    </div>
                    <div className="col-6">
                      <label className="form-label fw-bold small">Turno (horário de trabalho) *</label>
                      <select className="form-select" value={admitModal.shift_id}
                        onChange={(e) => setAdmitModal(m => ({ ...m, shift_id: e.target.value }))}>
                        <option value="">Selecione...</option>
                        {(admitOpcoes.turnos || []).map(o => (
                          <option key={o.id} value={o.id}>{o.nome}</option>
                        ))}
                      </select>
                    </div>
                    <div className="col-6 d-flex align-items-end">
                      <label className="form-check-label d-inline-flex align-items-center gap-2 mb-2"
                        style={{ cursor: 'pointer' }}>
                        <input type="checkbox" className="form-check-input mt-0"
                          checked={admitModal.is_clt}
                          onChange={(e) => setAdmitModal(m => ({ ...m, is_clt: e.target.checked }))} />
                        <span className="fw-bold small">É CLT?</span>
                      </label>
                    </div>
                    <div className="col-12">
                      <div className="alert alert-light border small mb-0">
                        <i className="fas fa-circle-info me-2 text-primary"></i>
                        O acesso ao sistema é criado com <b>login = CPF</b> e a senha padrão
                        da operação (<b>{(clienteNome || 'Empresa').split(' ')[0]}@1234</b>).
                        Tipo de registro e configuração de ponto/férias ficam com o padrão da
                        conta — ajuste no portal do PontoMais se precisar.
                      </div>
                    </div>
                  </div>
                )}
              </div>
              <div className="modal-footer bg-light">
                <button type="button" className="btn btn-secondary"
                  onClick={() => setAdmitModal(null)} disabled={salvandoAdmit}>
                  Cancelar
                </button>
                <button type="button" className="btn btn-success"
                  onClick={salvarAdmitir} disabled={salvandoAdmit || !admitOpcoes}>
                  <i className={`fas ${salvandoAdmit ? 'fa-spinner fa-spin' : 'fa-user-plus'} me-2`}></i>
                  {salvandoAdmit ? 'Criando...' : 'Criar no PontoMais'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ─── MODAL: corrigir a jornada do colaborador ─── */}
      {jornadaEdit && (
        <div className="modal" tabIndex="-1"
          style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 10001 }}>
          <div className="modal-dialog modal-dialog-centered" style={{ maxWidth: '480px' }}>
            <div className="modal-content shadow-lg">
              <div className="modal-header" style={{ backgroundColor: '#ffffff', borderBottom: '1px solid #e9ecef' }}>
                <div>
                  <h5 className="modal-title mb-0 fw-bold" style={{ color: '#212529' }}>
                    <i className="fas fa-user-clock me-2"></i>Jornada de trabalho
                  </h5>
                  <small style={{ color: '#6c757d' }}>{jornadaEdit.nome}</small>
                </div>
                <button type="button" className="btn-close"
                  onClick={() => setJornadaEdit(null)}></button>
              </div>

              <div className="modal-body">
                <div className="row g-3">
                  <div className="col-6">
                    <label className="form-label fw-bold small">Entrada *</label>
                    <input type="time" className="form-control" autoFocus value={jornadaEdit.entrada || ''}
                      onChange={(e) => setJornadaEdit(j => ({ ...j, entrada: e.target.value }))}
                      onKeyDown={(e) => { if (e.key === 'Escape') setJornadaEdit(null); }} />
                  </div>
                  <div className="col-6">
                    <label className="form-label fw-bold small">Saída *</label>
                    <input type="time" className="form-control" value={jornadaEdit.saida || ''}
                      onChange={(e) => setJornadaEdit(j => ({ ...j, saida: e.target.value }))} />
                  </div>
                  <div className="col-6">
                    <label className="form-label fw-bold small">Início do intervalo</label>
                    <input type="time" className="form-control" value={jornadaEdit.intervalo_inicio || ''}
                      onChange={(e) => setJornadaEdit(j => ({ ...j, intervalo_inicio: e.target.value }))} />
                  </div>
                  <div className="col-6">
                    <label className="form-label fw-bold small">Fim do intervalo</label>
                    <input type="time" className="form-control" value={jornadaEdit.intervalo_fim || ''}
                      onChange={(e) => setJornadaEdit(j => ({ ...j, intervalo_fim: e.target.value }))} />
                  </div>
                  <div className="col-12">
                    <label className="form-label small">
                      Saída na sexta-feira <span className="badge bg-light text-muted border">opcional</span>
                    </label>
                    <input type="time" className="form-control form-control-sm" style={{ maxWidth: 160 }}
                      value={jornadaEdit.sexta_saida || ''}
                      onChange={(e) => setJornadaEdit(j => ({ ...j, sexta_saida: e.target.value }))} />
                  </div>
                  <div className="col-12">
                    <label className="form-label fw-bold small mb-1">Dias em que não trabalha</label>
                    <div className="d-flex flex-wrap gap-2">
                      {['Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb', 'Dom'].map((rot, idx) => {
                        const marcado = (jornadaEdit.dias_folga || []).includes(idx);
                        return (
                          <label key={rot} className={`btn btn-sm ${marcado ? 'btn-secondary' : 'btn-light border'} mb-0`}
                            title={marcado ? `${rot}: folga fixa — não é cobrado e não tem jornada esperada` : `Marcar ${rot} como dia sem trabalho`}>
                            <input type="checkbox" className="d-none" checked={marcado}
                              onChange={(e) => setJornadaEdit(j => ({
                                ...j,
                                dias_folga: e.target.checked
                                  ? [...(j.dias_folga || []), idx].sort()
                                  : (j.dias_folga || []).filter(x => x !== idx),
                              }))} />
                            {marcado && <i className="fas fa-bed me-1"></i>}{rot}
                          </label>
                        );
                      })}
                    </div>
                    <div className="form-text">Ex.: aprendiz que não vem na sexta. O dia sai da conta de faltas e não espera jornada.</div>
                  </div>
                </div>
                {(() => {
                  const dia = jornadaSeg(jornadaEdit);
                  const sex = jornadaEdit.sexta_saida ? jornadaSeg(jornadaEdit, 'sexta_saida') : null;
                  return (
                    <div className="form-text mt-2">
                      {dia
                        ? <>Jornada de <b>{fmtHora(dia)}/dia</b> ({mensalDeSeg(dia)} por mês)
                            {sex ? <> · sexta <b>{fmtHora(sex)}</b></> : ''}
                            {(jornadaEdit.dias_folga || []).length > 0 && <> · não trabalha: <b>{jornadaEdit.dias_folga.map(i => ['seg', 'ter', 'qua', 'qui', 'sex', 'sáb', 'dom'][i]).join(', ')}</b></>}. Intervalo em branco = jornada corrida.</>
                        : 'Informe entrada e saída (a saída depois da entrada).'}
                      {' '}Hoje vale {jornadaEdit.mensalAtual}/mês.
                    </div>
                  );
                })()}

                {jornadaEdit.manual ? (
                  <div className="alert alert-warning small mt-3 mb-0">
                    <i className="fas fa-triangle-exclamation me-2"></i>
                    Esta jornada já está ajustada à mão e ignora o que vem da Convenia.
                    Use <b>Voltar para a Convenia</b> se o cadastro de lá foi corrigido.
                  </div>
                ) : (
                  <div className="alert alert-light border small mt-3 mb-0">
                    <i className="fas fa-circle-info me-2 text-primary"></i>
                    O ajuste vale só aqui no Saber e não altera a Convenia — corrija
                    lá também, senão o cadastro segue divergente.
                  </div>
                )}
              </div>

              <div className="modal-footer bg-light">
                {jornadaEdit.manual && (
                  <button className="btn btn-outline-danger btn-sm me-auto"
                    disabled={salvandoJornada}
                    onClick={() => salvarJornada(true)}
                    title="Remove o ajuste manual e volta a usar a jornada da Convenia">
                    <i className="fas fa-rotate-left me-2"></i>Voltar para a Convenia
                  </button>
                )}
                <button className="btn btn-secondary" disabled={salvandoJornada}
                  onClick={() => setJornadaEdit(null)}>
                  <i className="fas fa-times me-2"></i>Cancelar
                </button>
                <button className="btn btn-primary" disabled={salvandoJornada}
                  onClick={() => salvarJornada(false)}>
                  <i className={`fas ${salvandoJornada ? 'fa-spinner fa-spin' : 'fa-save'} me-2`}></i>
                  Salvar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BpoRhPonto;
